import {
    DriveItem,
    Event,
    FileAttachment,
    Message,
} from "@microsoft/microsoft-graph-types";
import { AuthContextValue, EventBus } from "../../../contexts";
import {
    IDateValue,
    inheritHttpStatus,
    logger,
    LOGS,
    makeHttpError,
} from "../../../utils";
import { encodeBase64 } from "../../../utils/base64/base64";
import { parseGraphDateTimeToIsoString } from "../../../utils/date/format-time";
import { buildEmailGraphQueryFromfilterObject } from "../../../utils/graphSearchQuery/graph-search-query";
import { normalizeString } from "../../../utils/normalizeString/normalizeString";
import { computeThrottleDelayMs } from "../../../utils/throttle";
import { AgendaFilter } from "../../agenda";
import {
    CreateAgendaItemData,
    UpdateAgendaItemData,
} from "../../agenda/types/agenda";
import { CustomViewSortOrder, ViewFilter } from "../../customView";
import {
    AttendeeResponseStatus,
    BodyContentType,
    EmailSearchFilter,
    EventMessageResponseType,
    EventMessageType,
    LinkedEventDetails,
    Mail,
    MailComponentSize,
    MailCopyRef,
    MailSend,
    MailSender,
    NavItem,
    ReplyForward,
} from "../../mail";
import {
    AgendaEventsPage,
    AgendaItemBody,
    BatchDeleteItem,
    BatchDeleteResult,
    BatchReadStatusItem,
    BatchReadStatusResult,
    ContactPerson,
    DataSearchResult,
    GraphBatchSubResponse,
    IDataApi,
    SendMailResult,
    SentMailsCriteria,
} from "../IDataApi";
import {
    applyDatePostFilter,
    buildFilterParts,
    buildKeywordsSearchParts,
    buildRecipientsSearchParts,
    consolidateFinalResults,
    consolidateSearchResults,
    encodeKqlSearchValue,
    escapeODataStringValue,
    isRecipientsOnlySearch,
    resolveSearchFlags,
    shouldSkipInboxRecipientsSearch,
} from "./graph-search-logic";
import { GraphThrottleError } from "./GraphThrottleError";
import {
    hasSentSearchTerms,
    isPastStart,
    selectSentResults,
    SENT_SEARCH_MAX_PAGES,
    sentFolderListUrl,
    sentFolderSearchUrls,
} from "./sent-search-logic";
import { BATCH_DELAY, BATCH_SIZE, graphApiUrl } from "./types/const";
import {
    BatchRequestData,
    BatchRequestItem,
    MailFlagStatus,
    MailFolder,
    MailImportance,
} from "./types/graph";

export class GraphManager implements IDataApi {
    // #region Properties

    protected eventBus: EventBus;

    protected authContext: AuthContextValue;

    private fetchingGraphUserProfile: Promise<any> | null = null;

    // #endregion Properties

    constructor(eventBus: EventBus, authContext: AuthContextValue) {
        logger.info(LOGS.GraphManager, "()");

        this.eventBus = eventBus;
        this.authContext = authContext;
    }

    // #region Event Handlers

    // #endregion Event Handlers

    // #region Initialisation and cleanup

    /**
     * @method cleanup
     * Cleans up all resources and stops all active processes for the GraphManager
     * ═══════════════════════════════════════════════════════════════════════════
     * @input {void}
     * @output {Promise<void>}
     * @public
     */
    public async _cleanup(): Promise<void> {
        logger.info(
            LOGS.GraphManager,
            "GraphManager.cleanup() - Starting cleanup process",
        );

        // Phase 1: Stop all active processes and timers
        try {
            logger.info(
                LOGS.GraphManager,
                "  Phase 1: Stopping active processes",
            );

            // Cancel pending Graph user profile fetch if in progress
            if (this.fetchingGraphUserProfile) {
                logger.info(
                    LOGS.GraphManager,
                    "    Cancelling pending graph user profile fetch",
                );
                // Note: Can't actually cancel the Promise, but we'll reset the reference
            }
        } catch (error) {
            logger.error(
                LOGS.GraphManager,
                `GraphManager.cleanup() - Error in Phase 1: ${error}`,
            );
        }

        // Phase 2: Clear internal state and caches
        try {
            logger.info(
                LOGS.GraphManager,
                "  Phase 2: Clearing internal state",
            );

            // Reset promises to their initial state
            this.fetchingGraphUserProfile = null;
        } catch (error) {
            logger.error(
                LOGS.GraphManager,
                `GraphManager.cleanup() - Error in Phase 2: ${error}`,
            );
        }

        // Phase 3: Nullify manager references
        try {
            logger.info(
                LOGS.GraphManager,
                "  Phase 3: Nullifying manager references",
            );

            // Set authContext reference to undefined
            this.authContext = undefined as any;
        } catch (error) {
            logger.error(
                LOGS.GraphManager,
                `GraphManager.cleanup() - Error in Phase 3: ${error}`,
            );
        }

        logger.info(
            LOGS.GraphManager,
            "GraphManager.cleanup() - Cleanup completed successfully",
        );
    }

    // #endregion Initialisation and cleanup

    // #region Public Methods

    // #region Mails

    // #region Search

    /**
     *
     * @param filter - Filter object to search for emails
     * @returns Array of Tuples {id: string; internetMessageId: string}
     */
    public async searchForNewMails(
        filter: EmailSearchFilter,
    ): Promise<{ id: string; internetMessageId: string }[]> {
        logger.info(
            [LOGS.GraphManager],
            `searchForNewMails with GraphManager...`,
        );

        try {
            const query = buildEmailGraphQueryFromfilterObject(filter);
            const response = await this.callGraphEndPoint(query);

            const result = response.value as Message[];
            logger.info(
                LOGS.GraphManager,
                `searchForNewMails Graph response`,
                response,
            );

            let filteredMails: Message[] = [];
            filteredMails = this._clientSideFilter(result, filter);

            if (result.length !== filteredMails.length) {
                logger.info(
                    [LOGS.GraphManager, LOGS.Perfs],
                    "Method _clientSideFilter is necessary",
                );
            }

            await this._fixTruncatedInternetMessageIds(
                filteredMails,
                "searchForNewMails",
            );

            const filteredMailsIds = filteredMails.map((mail: Message) => ({
                id: mail.id!,
                internetMessageId: mail.internetMessageId!,
            }));

            return filteredMailsIds;
        } catch (error) {
            logger.error(
                [LOGS.GraphManager],
                `Error in searchForNewMails: ${error}`,
            );
            if (error.details) {
                logger.info(
                    [LOGS.GraphManager],
                    "searchForNewMailsError",
                    error.details,
                );
            }
            throw error;
        }
    }

    /**
     * Recherche des emails selon les critères définis dans le ViewFilter
     * ═══════════════════════════════════════════════════════════════════════════
     *
     * WARNING : this query uses both Search and Filter ! This is undocumented but works currently
     * More info : https://altioremt.sharepoint.com/sites/ProjetBabaYaga/_layouts/Doc.aspx?sourcedoc={3B986CC6-47AD-4DB1-9E19-030ACF379110}&wd=target%28G%C3%A9n%C3%A9ral.one%7C418E3765-5447-4371-BC2F-072A42C850AD%2FSc%C3%A9narios%20possibles%20et%20pas%20possibles%20pour%20les%20Search%20de%20customView%7CFEDEFD35-C164-40DA-98B1-EBC9346E81D8%2F%29&wdpartid={853802CD-4DB0-0E6A-3C17-878248596665}{1}&wdsectionfileid={418E3765-5447-4371-BC2F-072A42C850AD}
     *             onenote:https://altioremt.sharepoint.com/sites/ProjetBabaYaga/SiteAssets/Bloc-notes%20de%20Projet%20Baba%20Yaga/Général.one#Scénarios%20possibles%20et%20pas%20possibles%20pour%20les%20Search%20de%20customView&section-id={418E3765-5447-4371-BC2F-072A42C850AD}&page-id={FEDEFD35-C164-40DA-98B1-EBC9346E81D8}&end
     *
     * @param viewFilter - Critères de recherche pour les emails (keywords, dates, utilisateurs)
     * @returns Liste d'internetMessageIds correspondant aux critères de recherche
     */
    public async searchInternetMessageIdsFromViewFilter(
        viewFilter: ViewFilter,
        limit: number | undefined = undefined,
    ): Promise<DataSearchResult[]> {
        logger.info(
            [LOGS.GraphManager],
            `searchMailsFromViewFilter with Advanced Mode: $filter + $search complex + $orderby...`,
        );

        try {
            // #region ======== Calcul des flags et construction des critères ========

            const currentUserEmail = this.authContext.getUser().email;
            const flags = resolveSearchFlags(viewFilter, currentUserEmail);
            const {
                hasSelfSender,
                hasOthersSenders,
                sendersBatchNeeded,
                searchBothFoldersForRecipients,
                searchBothFoldersNoParticipants,
                selfSenderFilterString,
                othersSenderFilterString,
                hasAnyFilterExceptDate,
            } = flags;

            const filterParts = buildFilterParts(viewFilter);

            let keywordsSearchParts: string[] = [];
            let keywordsPostFilterNeeded = false;
            if (viewFilter.keywords && viewFilter.keywords.length > 0) {
                keywordsSearchParts = buildKeywordsSearchParts(
                    viewFilter.keywords,
                );
                keywordsPostFilterNeeded = true;
            }

            let recipientsSearchParts: string[] = [];
            let recipientsPostFilterNeeded = false;
            if (
                viewFilter.identifiedRecipients &&
                viewFilter.identifiedRecipients.length > 0
            ) {
                const resolved = buildRecipientsSearchParts(
                    viewFilter.identifiedRecipients,
                    currentUserEmail,
                    flags,
                );
                recipientsSearchParts = resolved.parts;
                recipientsPostFilterNeeded = resolved.needed;
            }

            let sendersBatchRequests: BatchRequestItem[] = [];

            // #endregion ======== Calcul des flags et construction des critères ========

            // #region ======== CONSTRUCTION DE mainQuery AVEC SELECT ========

            // Base de la requête
            let mainQuery =
                "/me/mailFolders/inbox/messages?$select=id,internetMessageId,sentDateTime";

            // Si un filtre existe sur les destinataires, ajouter les champs concernés au 'select' pour post filter
            if (recipientsPostFilterNeeded) {
                mainQuery += ",toRecipients,ccRecipients,bccRecipients";
            }

            // Si un filtre existe sur les keywords, ajouter les champs concernés au 'select' pour post filter
            if (keywordsPostFilterNeeded) {
                mainQuery += ",subject,uniqueBody";
            }

            // #endregion ======== CONSTRUCTION DE mainQuery AVEC SELECT ========

            // #region ======== CONSTRUCTION DE mainQueryWithFiltersAndTopAndOrderBy avec FILTER ========

            let mainQueryWithFiltersAndTopAndOrderBy = mainQuery;

            // Construction des filters autres que les dates

            // Construction du filtre
            const mainQueryWithFilters = mainQuery.concat(
                `&$filter=${filterParts.join(" and ")}`,
            );

            // Limitation du nombre de résultats
            const limitOrDefaultLimit = limit || 100;
            const mainQueryWithFiltersAndTop = mainQueryWithFilters.concat(
                `&$top=${limitOrDefaultLimit}`,
            );

            // Ordre de tri
            mainQueryWithFiltersAndTopAndOrderBy =
                mainQueryWithFiltersAndTop.concat(
                    // Espace littéral, JAMAIS `%20` : l'URL contient déjà des
                    // espaces dans `$filter`, donc iOS lui applique une passe de
                    // percent-encoding qui ré-encoderait un `%` en `%25` → Graph 400.
                    `&$orderby=sentDateTime ${viewFilter.sortOrder || CustomViewSortOrder.DESC}`,
                );

            // Construction des variantes pour le batch senders si nécessaire
            let mainQueryInboxWithFiltersAndTopAndOrderBy = "";
            let mainQuerySentItemsWithFiltersAndTopAndOrderBy = "";

            if (sendersBatchNeeded) {
                const limitOrDefaultLimit = limit || 100;
                // Espace littéral (cf. commentaire sur l'appel simple ci-dessus).
                const orderBy = `&$orderby=sentDateTime ${viewFilter.sortOrder || CustomViewSortOrder.DESC}`;

                if (
                    searchBothFoldersForRecipients ||
                    searchBothFoldersNoParticipants
                ) {
                    // Cas spécial - pas de filtre sender, chercher dans les deux
                    mainQueryInboxWithFiltersAndTopAndOrderBy =
                        mainQuery +
                        `&$filter=${filterParts.join(" and ")}` +
                        `&$top=${limitOrDefaultLimit}` +
                        orderBy;

                    const sentQuery = mainQuery.replace(
                        "/inbox/",
                        "/sentItems/",
                    );
                    mainQuerySentItemsWithFiltersAndTopAndOrderBy =
                        sentQuery +
                        `&$filter=${filterParts.join(" and ")}` +
                        `&$top=${limitOrDefaultLimit}` +
                        orderBy;
                } else {
                    // Cas standard avec senders
                    if (hasOthersSenders) {
                        const inboxFilterParts = [
                            ...filterParts,
                            othersSenderFilterString,
                        ];
                        mainQueryInboxWithFiltersAndTopAndOrderBy =
                            mainQuery +
                            `&$filter=${inboxFilterParts.join(" and ")}` +
                            `&$top=${limitOrDefaultLimit}` +
                            orderBy;
                    }

                    if (hasSelfSender) {
                        const sentFilterParts = [
                            ...filterParts,
                            selfSenderFilterString,
                        ];
                        const sentQuery = mainQuery.replace(
                            "/inbox/",
                            "/sentItems/",
                        );
                        mainQuerySentItemsWithFiltersAndTopAndOrderBy =
                            sentQuery +
                            `&$filter=${sentFilterParts.join(" and ")}` +
                            `&$top=${limitOrDefaultLimit}` +
                            orderBy;
                    }
                }
            }

            // #endregion ======== CONSTRUCTION DE mainQueryWithFiltersAndTopAndOrderBy avec FILTER ========

            // #region ======== EXECUTION DE mainQueryWithFiltersAndTopAndOrderBy ========

            let consolidatedMainqueryResults: any = [];
            let mainQueryWasExecuted = false;

            // Déterminer si on doit exécuter la MainQuery
            const isRecipientsOnly = isRecipientsOnlySearch(viewFilter);
            let shouldExecuteMainQuery = false;
            let mainQuerySkipReason = "";

            if (isRecipientsOnly) {
                // Cas spécial : skip complètement la MainQuery
                shouldExecuteMainQuery = false;
                mainQueryWasExecuted = false;
                mainQuerySkipReason = "RECIPIENTS_ONLY_SEARCH";
            } else if (
                hasAnyFilterExceptDate ||
                viewFilter.startDate ||
                viewFilter.endDate
            ) {
                // Cas normal : exécuter la MainQuery
                shouldExecuteMainQuery = true;
                mainQueryWasExecuted = true;
            } else {
                // Pas de filtres du tout
                shouldExecuteMainQuery = false;
                mainQueryWasExecuted = false;
                mainQuerySkipReason = "NO_FILTERS";

                logger.info(
                    LOGS.TESTS,
                    "⚠️ SKIPPING MainQuery - No filters at all",
                );
                logger.info(
                    LOGS.TESTS,
                    "  → Will use ONLY search results if any",
                );
            }

            // Si on doit exécuter la mainQuery
            if (shouldExecuteMainQuery) {
                if (sendersBatchNeeded) {
                    // Construction et exécution du batch
                    let requestId = 0;

                    if (hasOthersSenders || searchBothFoldersNoParticipants) {
                        sendersBatchRequests.push({
                            id: (requestId++).toString(),
                            method: "GET",
                            url: mainQueryInboxWithFiltersAndTopAndOrderBy,
                        });
                    }

                    if (hasSelfSender || searchBothFoldersNoParticipants) {
                        sendersBatchRequests.push({
                            id: (requestId++).toString(),
                            method: "GET",
                            url: mainQuerySentItemsWithFiltersAndTopAndOrderBy,
                        });
                    }

                    const batchRequestData: BatchRequestData = {
                        requests: sendersBatchRequests,
                    };

                    const batchResults =
                        await this.sendBatchRequest(batchRequestData);

                    // Consolidation avec pagination
                    for (const response of batchResults.responses) {
                        if (response.body?.value) {
                            consolidatedMainqueryResults = [
                                ...consolidatedMainqueryResults,
                                ...response.body.value,
                            ];

                            let nextLink = response.body["@odata.nextLink"];

                            while (nextLink && !limit) {
                                const relativeUrl = nextLink.replace(
                                    graphApiUrl,
                                    "",
                                );
                                const nextPageResponse =
                                    await this.callGraphEndPoint(relativeUrl);

                                if (
                                    nextPageResponse &&
                                    nextPageResponse.value
                                ) {
                                    consolidatedMainqueryResults = [
                                        ...consolidatedMainqueryResults,
                                        ...nextPageResponse.value,
                                    ];
                                    nextLink =
                                        nextPageResponse["@odata.nextLink"];
                                } else {
                                    nextLink = null;
                                }
                            }
                        }
                    }

                    // Éliminer les doublons
                    consolidatedMainqueryResults =
                        consolidatedMainqueryResults.filter(
                            (message: any, index: any, self: any) =>
                                index ===
                                self.findIndex(
                                    (m: any) =>
                                        m.internetMessageId ===
                                        message.internetMessageId,
                                ),
                        );
                } else {
                    // Le log `decodeURIComponent(url)` qui se trouvait ici a été retiré :
                    // l'URL ne contient plus d'échappement manuel à décoder, et
                    // `decodeURIComponent` lève sur un `%` littéral (URIError dans un log).
                    const response = await this.callGraphEndPoint(
                        mainQueryWithFiltersAndTopAndOrderBy,
                    );

                    if (response && response.value) {
                        consolidatedMainqueryResults = response.value;

                        let nextLink = response["@odata.nextLink"];
                        while (nextLink && !limit) {
                            const relativeUrl = nextLink.replace(
                                graphApiUrl,
                                "",
                            );
                            const nextPageResponse =
                                await this.callGraphEndPoint(relativeUrl);

                            if (nextPageResponse && nextPageResponse.value) {
                                consolidatedMainqueryResults = [
                                    ...consolidatedMainqueryResults,
                                    ...nextPageResponse.value,
                                ];
                                nextLink = nextPageResponse["@odata.nextLink"];
                            } else {
                                nextLink = null;
                            }
                        }
                    }
                }
            } else {
            }

            // #endregion ======== EXECUTION DE mainQueryWithFiltersAndTopAndOrderBy ========

            // #region ======== CONSTRUCTION & EXECUTION DE mainQueryWithSearch SEARCH KEYWORDS ========

            // Execution des requêtes Search Keyword si il y en a
            let consolidatedKeywordsResults: any = [];
            if (keywordsPostFilterNeeded) {
                // Build list of requests for Batch
                const keywordsRequests: BatchRequestItem[] = [];
                let keywordsRequestID = 0;

                keywordsSearchParts.forEach((keyword) => {
                    // Recherche dans inbox
                    if (
                        hasOthersSenders ||
                        searchBothFoldersForRecipients ||
                        searchBothFoldersNoParticipants
                    ) {
                        keywordsRequests.push({
                            id: (keywordsRequestID++).toString(),
                            method: "GET",
                            url: mainQuery.concat(
                                `&$search=${encodeKqlSearchValue(keyword)}`,
                            ),
                        });
                    }

                    // Recherche dans sentItems aussi si nécessaire
                    if (
                        hasSelfSender ||
                        searchBothFoldersForRecipients ||
                        searchBothFoldersNoParticipants
                    ) {
                        const sentQuery = mainQuery.replace(
                            "/inbox/",
                            "/sentItems/",
                        );
                        keywordsRequests.push({
                            id: (keywordsRequestID++).toString(),
                            method: "GET",
                            url: sentQuery.concat(
                                `&$search=${encodeKqlSearchValue(keyword)}`,
                            ),
                        });
                    }
                });

                // Call Graph api with batch
                const batchRequestData: BatchRequestData = {
                    requests: keywordsRequests,
                };
                const batchResultsForKeywordsRequest =
                    await this.sendBatchRequest(batchRequestData);

                // Consolidation avec pagination
                for (const response of batchResultsForKeywordsRequest.responses) {
                    if (response.body?.value) {
                        consolidatedKeywordsResults = [
                            ...consolidatedKeywordsResults,
                            ...response.body.value,
                        ];

                        let nextLink = response.body["@odata.nextLink"];

                        while (nextLink && !limit) {
                            const relativeUrl = nextLink.replace(
                                graphApiUrl,
                                "",
                            );
                            const nextPageResponse =
                                await this.callGraphEndPoint(relativeUrl);

                            if (nextPageResponse && nextPageResponse.value) {
                                consolidatedKeywordsResults = [
                                    ...consolidatedKeywordsResults,
                                    ...nextPageResponse.value,
                                ];
                                nextLink = nextPageResponse["@odata.nextLink"];
                            } else {
                                nextLink = null;
                            }
                        }
                    }
                }

                // Join and unique results
                consolidatedKeywordsResults =
                    consolidatedKeywordsResults.filter(
                        (message: any, index: any, self: any) =>
                            index ===
                            self.findIndex(
                                (m: any) =>
                                    m.internetMessageId ===
                                    message.internetMessageId,
                            ),
                    );
            }

            // #endregion ======== CONSTRUCTION & EXECUTION DE mainQueryWithSearch SEARCH KEYWORDS ========

            // #region ======== CONSTRUCTION & EXECUTION DE mainQueryWithSearch SEARCH RECIPIENTS ========

            // Execution des requêtes Search Recipients si il y en a
            let consolidatedRecipientsResults: any = [];
            const inboxRecipientsSkipped = shouldSkipInboxRecipientsSearch(
                recipientsSearchParts,
                currentUserEmail,
                flags,
            );

            if (recipientsPostFilterNeeded) {
                const recipientsRequests: BatchRequestItem[] = [];
                let requestID = 0;

                if (searchBothFoldersForRecipients) {
                    // Recipients + autres filtres (dates, readStatus…), sans senders explicites.
                    // → Chercher tous les recipients dans inbox ET sentItems sans exclure le current user.
                    // → La consolidation finale sera une INTERSECTION avec la mainQuery (date-filtrée).
                    const sentQueryBase = mainQuery.replace(
                        "/inbox/",
                        "/sentItems/",
                    );
                    recipientsSearchParts.forEach((recipient) => {
                        recipientsRequests.push({
                            id: (requestID++).toString(),
                            method: "GET",
                            url: mainQuery.concat(
                                `&$search=${encodeKqlSearchValue(recipient)}`,
                            ),
                        });
                        recipientsRequests.push({
                            id: (requestID++).toString(),
                            method: "GET",
                            url: sentQueryBase.concat(
                                `&$search=${encodeKqlSearchValue(recipient)}`,
                            ),
                        });
                    });
                } else {
                    // Préparer les listes de destinataires selon le dossier
                    const recipientsForInbox = recipientsSearchParts.filter(
                        (recipient) => {
                            const recipientEmail = recipient.replace(
                                /^"recipients:|"$/g,
                                "",
                            );
                            return (
                                recipientEmail.toLowerCase() !==
                                currentUserEmail.toLowerCase()
                            );
                        },
                    );

                    // Recherche dans inbox si hasOthers ET si il reste des destinataires après filtrage
                    if (
                        (hasOthersSenders || searchBothFoldersNoParticipants) &&
                        recipientsForInbox.length > 0
                    ) {
                        recipientsForInbox.forEach((recipient) => {
                            recipientsRequests.push({
                                id: (requestID++).toString(),
                                method: "GET",
                                url: mainQuery.concat(
                                    `&$search=${encodeKqlSearchValue(recipient)}`,
                                ),
                            });
                        });
                    }

                    // Recherche dans sentItems si hasSelf (garder tous les destinataires)
                    if (hasSelfSender || searchBothFoldersNoParticipants) {
                        recipientsSearchParts.forEach((recipient) => {
                            recipientsRequests.push({
                                id: (requestID++).toString(),
                                method: "GET",
                                url: mainQuery
                                    .replace("/inbox/", "/sentItems/")
                                    .concat(
                                        `&$search=${encodeKqlSearchValue(recipient)}`,
                                    ),
                            });
                        });
                    }
                }

                // Si aucune requête à faire, pas de batch
                if (recipientsRequests.length === 0) {
                    recipientsPostFilterNeeded = false;
                } else {
                    // Call Graph api with batch
                    const batchRequestData: BatchRequestData = {
                        requests: recipientsRequests,
                    };
                    const batchResultsForRecipientsRequest =
                        await this.sendBatchRequest(batchRequestData);

                    // Consolidation avec pagination
                    for (const response of batchResultsForRecipientsRequest.responses) {
                        if (response.body?.value) {
                            consolidatedRecipientsResults = [
                                ...consolidatedRecipientsResults,
                                ...response.body.value,
                            ];

                            let nextLink = response.body["@odata.nextLink"];

                            while (nextLink && !limit) {
                                const relativeUrl = nextLink.replace(
                                    graphApiUrl,
                                    "",
                                );
                                const nextPageResponse =
                                    await this.callGraphEndPoint(relativeUrl);

                                if (
                                    nextPageResponse &&
                                    nextPageResponse.value
                                ) {
                                    consolidatedRecipientsResults = [
                                        ...consolidatedRecipientsResults,
                                        ...nextPageResponse.value,
                                    ];
                                    nextLink =
                                        nextPageResponse["@odata.nextLink"];
                                } else {
                                    nextLink = null;
                                }
                            }
                        }
                    }

                    // Join and unique results
                    consolidatedRecipientsResults =
                        consolidatedRecipientsResults.filter(
                            (message: any, index: any, self: any) =>
                                index ===
                                self.findIndex(
                                    (m: any) =>
                                        m.internetMessageId ===
                                        message.internetMessageId,
                                ),
                        );
                }
            }

            // #endregion ======== CONSTRUCTION & EXECUTION DE mainQueryWithSearch SEARCH RECIPIENTS ========

            // #region ======== JOIN des results des SEARCHS ========

            const searchConsolidation = consolidateSearchResults(
                consolidatedKeywordsResults,
                consolidatedRecipientsResults,
                keywordsPostFilterNeeded,
                recipientsPostFilterNeeded,
            );
            let consolidatedSearchResults = searchConsolidation.results;
            const noSearchResultsToConsolidate = searchConsolidation.noResults;

            // #endregion ======== JOIN des results des SEARCHS ========

            // #region ======== POST-FILTRE dates sur les résultats $search ========
            // Les requêtes $search ne supportent pas $filter, donc elles ignorent les
            // contraintes de dates. On applique le filtre ici côté client pour garantir
            // la cohérence avec les critères de date fournis dans le ViewFilter.
            if (!noSearchResultsToConsolidate) {
                consolidatedSearchResults = applyDatePostFilter(
                    consolidatedSearchResults,
                    viewFilter.startDate,
                    viewFilter.endDate,
                );
            }
            // #endregion ======== POST-FILTRE dates sur les résultats $search ========

            // #region ======== JOIN final de main query et des SEARCHS ========

            const fullConsolidatedResults = consolidateFinalResults({
                mainResults: consolidatedMainqueryResults,
                searchResults: consolidatedSearchResults,
                mainWasExecuted: mainQueryWasExecuted,
                noSearchResults: noSearchResultsToConsolidate,
                inboxRecipientsSkipped: inboxRecipientsSkipped,
                keywordsNeeded: keywordsPostFilterNeeded,
            });

            // #endregion ======== JOIN final de main query et des SEARCHS ========

            // #region ======== ORDERBY et SLICE au nombre max souhaité ========

            // Order by
            const orderedFullConsolidatedResults = [
                ...fullConsolidatedResults,
            ].sort((a, b) => {
                const dateA: number = new Date(a.sentDateTime).getTime();
                const dateB: number = new Date(b.sentDateTime).getTime();
                return viewFilter.sortOrder === "DESC"
                    ? dateB - dateA
                    : dateA - dateB;
            });

            // Top — [AB#1727] Ne tronquer QUE si un `limit` explicite est fourni (assistant IA
            // borné, cf. ConversationManager). Sans `limit`, renvoyer TOUTE la membership : le
            // `$top = limit || 100` n'est qu'une taille de page (la pagination @odata.nextLink
            // récupère déjà toutes les pages). Auparavant `slice(0, limit || 100)` amputait
            // silencieusement la membership d'une CustomView à 100 (bug AB#1727 : 1188 → 100).
            const limitedOrderedFullConsolidatedResults = limit
                ? orderedFullConsolidatedResults.slice(0, limit)
                : orderedFullConsolidatedResults;

            // #endregion ======== ORDERBY et SLICE au nombre max souhaité ========

            // #region ======== MAPPING final des results ========

            let finalMatchingMails = limitedOrderedFullConsolidatedResults.map(
                (item: any) => {
                    let dataSearchResult: DataSearchResult = {
                        internetMessageId: item.internetMessageId,
                    };

                    // La date sert déjà au tri local ci-dessus : on la conserve, l'API en a besoin.
                    if (item.sentDateTime) {
                        dataSearchResult.sentDateTime = item.sentDateTime;
                    }

                    if (item.subject) {
                        dataSearchResult.subject = item.subject;
                    }
                    if (item.uniqueBody) {
                        dataSearchResult.uniqueBodyContent =
                            item.uniqueBody?.content;
                    }
                    if (item.toRecipients) {
                        dataSearchResult.dest = item.toRecipients;
                    }
                    if (item.ccRecipients) {
                        dataSearchResult.cc = item.ccRecipients;
                    }
                    if (item.bccRecipients) {
                        dataSearchResult.bcc = item.bccRecipients;
                    }

                    return dataSearchResult;
                },
            );
            logger.info(
                [LOGS.TESTS],
                `  finalMatchingMails`,
                finalMatchingMails,
            );

            // #endregion ======== MAPPING final des results ========

            // #region ======== FIX internetMessageIds truncated ========

            const truncatedResults = finalMatchingMails.filter(
                (item) =>
                    item.internetMessageId &&
                    !item.internetMessageId.endsWith(">"),
            );

            if (truncatedResults.length > 0) {
                for (const truncatedItem of truncatedResults) {
                    try {
                        const fixParams = new URLSearchParams();
                        fixParams.append(
                            "$filter",
                            `startsWith(internetMessageId, '${escapeODataStringValue(truncatedItem.internetMessageId)}')`,
                        );
                        const fixQuery = `/me/messages?${fixParams.toString()}`;
                        const fixResult =
                            await this.callGraphEndPoint(fixQuery);

                        if (fixResult.value[0]?.internetMessageId) {
                            truncatedItem.internetMessageId =
                                fixResult.value[0].internetMessageId;
                        }
                    } catch (error) {
                        logger.error(
                            [LOGS.GraphManager],
                            `Failed to fix truncated ID for message ${truncatedItem.internetMessageId}: ${error}`,
                        );
                    }
                }
            }

            // #endregion ======== FIX internetMessageIds truncated ========

            logger.info(
                [LOGS.GraphManager],
                `  searchMailsFromViewFilter - found ${finalMatchingMails.length} emails matching criteria`,
            );

            return finalMatchingMails;
        } catch (error) {
            // Log détaillé pour debug en cas d'erreur
            if (
                error instanceof Error &&
                error.message.includes("SearchWithFilter")
            ) {
                logger.error(
                    [LOGS.GraphManager],
                    `  SearchWithFilter error detected. Check if search query contains AND/OR operators.`,
                );
            }

            logger.error(
                [LOGS.GraphManager],
                `Error in searchInternetMessageIdsFromViewFilter: ${error}`,
            );
            if (error.details) {
                logger.info([LOGS.GraphManager], "Error", error.details);
            }
            throw error;
        }
    }

    public async searchContact(
        searchString: string,
        top: number = 20,
    ): Promise<string[]> {
        const people = await this.searchContactPeople(searchString, top);
        return people.map((person) => person.email);
    }

    /**
     * Les contacts du carnet Microsoft (`/me/people` et l'annuaire) dont chaque mot cherché commence un
     * mot du nom ou de l'adresse, chacun avec son nom affiché. Adresses en minuscules, sans doublon,
     * `/me/people` d'abord. `searchContact` en rend les adresses seules.
     */
    public async searchContactPeople(
        searchString: string,
        top: number = 20,
    ): Promise<ContactPerson[]> {
        logger.info(
            [LOGS.GraphManager],
            `searchContact for ${searchString}...`,
        );

        try {
            const peopleEndPoint = `/me/people/?$search="${encodeURIComponent(searchString)}"&$select=displayName,givenName,surname,scoredEmailAddresses&$top=${top}`;
            logger.info(
                [LOGS.GraphManager],
                `searchContact endpoints: people=${peopleEndPoint} + /search/query`,
            );

            const [peopleResponse, searchResponse] = await Promise.all([
                this.callGraphEndPoint(peopleEndPoint),
                this.callGraphEndPoint("/search/query", "POST", {
                    requests: [
                        {
                            entityTypes: ["person"],
                            query: { queryString: searchString },
                            from: 0,
                            size: top,
                        },
                    ],
                }).catch((error: any) => {
                    logger.warn(
                        [LOGS.GraphManager],
                        `searchContact: /search/query fallback (${error})`,
                    );
                    return null;
                }),
            ]);

            logger.info(
                [LOGS.GraphManager],
                "People contacts raw",
                peopleResponse.value,
            );
            logger.info(
                [LOGS.GraphManager],
                "Search contacts raw",
                searchResponse?.value,
            );

            // Normalize search string and split into individual words (supports multi-word queries)
            const searchWords = normalizeString(searchString)
                .split(/\s+/)
                .filter((w) => w.length > 0);

            // Returns true if every search word starts at a word boundary in at least one of the provided normalized fields.
            // Left boundary only — enables prefix matching (e.g. "nicol" matches "nicolas", "nicole").
            // Left boundary still prevents false suffix matches (e.g. "eric" ≠ "frederic" because "e" is preceded by "d").
            const matchesAllWords = (fields: string[]): boolean =>
                searchWords.every((word) => {
                    const wordRegex = new RegExp(
                        `(^|[\\s@._-])${word.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}`,
                        "i",
                    );
                    return fields.some((field) => wordRegex.test(field));
                });

            // /me/people/ results
            const people: ContactPerson[] = peopleResponse.value
                .filter((result: any) => {
                    const email = result.scoredEmailAddresses[0]?.address;
                    return (
                        email &&
                        matchesAllWords([
                            normalizeString(email),
                            normalizeString(result.displayName ?? ""),
                            normalizeString(result.givenName ?? ""),
                            normalizeString(result.surname ?? ""),
                        ])
                    );
                })
                .map((result: any) => ({
                    email: result.scoredEmailAddresses[0].address as string,
                    displayName: (result.displayName ?? "") as string,
                }));

            // /search/query results — same filter eliminates Microsoft fuzzy-match false positives (e.g. "Laura" when searching "lara")
            const searched: ContactPerson[] = (
                searchResponse?.value?.[0]?.hitsContainers?.[0]?.hits ?? []
            ).reduce((acc: ContactPerson[], hit: any) => {
                const r = hit.resource;
                const email: string | undefined =
                    r?.emailAddresses?.[0]?.address ??
                    r?.scoredEmailAddresses?.[0]?.address ??
                    r?.userPrincipalName;
                if (
                    email &&
                    !email.includes("yammer.com") &&
                    matchesAllWords([
                        normalizeString(r?.displayName ?? ""),
                        normalizeString(r?.givenName ?? ""),
                        normalizeString(r?.surname ?? ""),
                        normalizeString(email),
                    ])
                )
                    acc.push({ email, displayName: r?.displayName ?? "" });
                return acc;
            }, []);

            logger.info([LOGS.GraphManager], "People emails", people);
            logger.info([LOGS.GraphManager], "Search emails", searched);

            // Une adresse une seule fois (en minuscules), à sa première apparition.
            const unique = new Map<string, ContactPerson>();
            for (const person of [...people, ...searched]) {
                const email = person.email.toLowerCase();
                if (!unique.has(email)) {
                    unique.set(email, {
                        email,
                        displayName: person.displayName,
                    });
                }
            }
            const uniquePeople = [...unique.values()];
            logger.info(
                [LOGS.GraphManager],
                "Unique merged emails",
                uniquePeople.map((person) => person.email),
            );

            return uniquePeople;
        } catch (error) {
            logger.error(
                [LOGS.GraphManager],
                `Error in searchContact: ${error}`,
            );
            if (error.details) {
                logger.info(
                    [LOGS.GraphManager],
                    "SearchContactError",
                    error.details,
                );
            }
            throw error;
        }
    }

    // #endregion Search

    //#region Getters

    /** Champs `$select` d'une copie détaillée. Partagé par `getMailById` et `getMailsByIds`
     *  pour garantir des objets `Mail` strictement identiques (parité de mapping). */
    private static readonly _MAIL_DETAIL_SELECT =
        "id,internetMessageId,conversationId,subject,body,bodyPreview,uniqueBody,from,toRecipients,ccRecipients,bccRecipients,sentDateTime,isRead,importance,hasAttachments,parentFolderId";

    public async getMailById(mailId: string): Promise<Mail> {
        const query = `/me/messages/${encodeURIComponent(mailId)}?$select=${GraphManager._MAIL_DETAIL_SELECT}`;

        const response = await this.callGraphEndPoint(query);

        const mail: Mail = this.mapGraphMailToIMail(response);

        return mail;
    }

    /**
     * Récupère un lot de copies par leur id Graph via le JSON Batching (`POST /$batch`), en
     * chunks de 20 (limite Graph). Même `$select` que `getMailById` → objets `Mail` identiques.
     *
     * Isolation par item (calqué sur `batchSetReadStatus`) :
     *   • 2xx        → mappé et conservé dans `mails`
     *   • 429 / 5xx  → rejoué (backoff exponentiel par chunk)
     *   • autre 4xx  → échec définitif (ex. 404 : `graphId` périmé) → remonté dans `failedIds`
     *
     * Ne lève jamais sur un item : l'appelant (vue Dossier) décide quoi re-tenter via sa couche
     * retry slot-aware. Pas de `_fixTruncatedInternetMessageIds` (parité stricte avec `getMailById` :
     * ce fix ne concerne que le listing `$search`, pas les reads par id).
     *
     * @returns `mails` = copies récupérées et mappées ; `failedIds` = graphIds non récupérés.
     */
    public async getMailsByIds(
        mailIds: string[],
        maxRetries = 3,
    ): Promise<{ mails: Mail[]; failedIds: string[] }> {
        const GRAPH_BATCH_MAX = 20; // limite dure du $batch Graph
        const BATCH_DELAY_MS = 500; // pause entre chunks consécutifs
        const mails: Mail[] = [];
        const failedIds: string[] = [];

        for (let i = 0; i < mailIds.length; i += GRAPH_BATCH_MAX) {
            let chunk = mailIds.slice(i, i + GRAPH_BATCH_MAX);
            let attempt = 0;
            // Headers de la sous-réponse 429 la plus récente → délai piloté par Retry-After.
            let lastThrottleHeaders:
                | GraphBatchSubResponse["headers"]
                | undefined;

            // Boucle de retry par chunk : `chunk` rétrécit (seuls les items 429/5xx sont conservés).
            while (chunk.length > 0 && attempt < maxRetries) {
                if (attempt > 0) {
                    // Helper partagé : Retry-After (sous-réponse) sinon backoff exponentiel + jitter.
                    const retryDelay = computeThrottleDelayMs(
                        lastThrottleHeaders,
                        attempt,
                        { maxDelayMs: 16000 },
                    );
                    logger.warn(
                        [LOGS.GraphManager],
                        `getMailsByIds: retry ${attempt}/${maxRetries - 1} — ${chunk.length} item(s), waiting ${retryDelay}ms`,
                    );
                    await new Promise((resolve) =>
                        setTimeout(resolve, retryDelay),
                    );
                }

                // Une sous-requête GET par item ; `id` (= index dans le chunk) remappe les réponses.
                const batchPayload = {
                    requests: chunk.map((graphId, idx) => ({
                        id: String(idx),
                        method: "GET",
                        url: `/me/messages/${encodeURIComponent(graphId)}?$select=${GraphManager._MAIL_DETAIL_SELECT}`,
                    })),
                };

                const batchResponse = await this.callGraphEndPoint(
                    "/$batch",
                    "POST",
                    batchPayload,
                );
                const retryable: string[] = [];
                lastThrottleHeaders = undefined;

                batchResponse.responses.forEach(
                    (resp: GraphBatchSubResponse) => {
                        const graphId = chunk[parseInt(resp.id, 10)];
                        if (resp.status >= 200 && resp.status < 300) {
                            mails.push(this.mapGraphMailToIMail(resp.body));
                        } else if (resp.status === 429 || resp.status >= 500) {
                            retryable.push(graphId);
                            if (resp.status === 429 && resp.headers) {
                                lastThrottleHeaders = resp.headers;
                            }
                        } else {
                            logger.warn(
                                [LOGS.GraphManager],
                                `getMailsByIds: ${graphId} → HTTP ${resp.status} (non-retryable)`,
                            );
                            failedIds.push(graphId);
                        }
                    },
                );

                chunk = retryable;
                attempt++;
            }

            // Items ayant épuisé toutes les tentatives (429/5xx persistants).
            for (const graphId of chunk) {
                logger.warn(
                    [LOGS.GraphManager],
                    `getMailsByIds: ${graphId} failed after ${maxRetries} attempt(s)`,
                );
                failedIds.push(graphId);
            }

            if (i + GRAPH_BATCH_MAX < mailIds.length) {
                await new Promise((resolve) =>
                    setTimeout(resolve, BATCH_DELAY_MS),
                );
            }
        }

        return { mails, failedIds };
    }

    public async getMailAttachments(
        mailId: string,
        parentFolderId?: string,
    ): Promise<FileAttachment[]> {
        logger.info(
            LOGS.GraphManager,
            `log getMailAttachments() for mail ${mailId}`,
        );

        // Folder-scoped endpoint is more reliable than the global /me/messages/{id} endpoint:
        // it bypasses the Graph search index and addresses the message directly in its folder.
        // This avoids 404s caused by index lag, recently received messages, or nested folders.
        const baseEndpoint = parentFolderId
            ? `/me/mailFolders/${encodeURIComponent(parentFolderId)}/messages/${encodeURIComponent(mailId)}`
            : `/me/messages/${encodeURIComponent(mailId)}`;

        try {
            const attachmentsRequest = `${baseEndpoint}/attachments`;
            const attachmentsResult =
                await this.callGraphEndPoint(attachmentsRequest);

            if (attachmentsResult.error) {
                logger.error(
                    LOGS.GraphManager,
                    "Error during Mail Attachements fetching",
                );
                throw new Error("Error during Mail Attachements fetching");
            }

            const attachments = attachmentsResult.value;

            const processedAttachments: FileAttachment[] = await Promise.all(
                attachments.map(async (attachment: any) => {
                    if (
                        attachment["@odata.type"] ===
                        "#microsoft.graph.itemAttachment"
                    ) {
                        // Retrive content of itemAttachments (brute text) to convert in base64 to display it in Front
                        try {
                            const itemAttachmentContentRequest = `${baseEndpoint}/attachments/${attachment.id}/$value`;
                            const itemAttachmentContent =
                                await this.callGraphEndPoint(
                                    itemAttachmentContentRequest,
                                );
                            const itemAttachmentContentEncoded = encodeBase64(
                                itemAttachmentContent,
                            );

                            attachment.contentBytes =
                                itemAttachmentContentEncoded;
                        } catch (error) {
                            logger.error(
                                [LOGS.GraphManager],
                                `Error fetching raw content for itemAttachment ${attachment.id}: ${error}`,
                            );
                        }
                    }

                    return attachment as FileAttachment;
                }),
            );

            return processedAttachments;
        } catch (error) {
            logger.error(
                [LOGS.GraphManager],
                `Error fetching attachments for mail ${mailId}: ${error}`,
            );
            throw error; // Relance l'erreur pour qu'elle soit gérée par l'appelant
        }
    }

    /**
     * Fetch mails with pagination support
     * ═══════════════════════════════════════════════════════════
     *
     * @param options                       - Configuration options
     * @param options.skip                  - Number of items to skip (for pagination)
     * @param options.top                   - Maximum number of items to return
     * @returns                             - MailCopyRef[] (une réf de copie par item du dossier : internetMessageId + id Graph volatil + parentFolderId opaque)
     */
    public async fetchGeneralInternetMessageIds(
        mailFolder: MailFolder = MailFolder.Inbox,
        top: number = 30,
        skip: number = 0,
    ): Promise<MailCopyRef[]> {
        // const startTime = performance.now();
        logger.info(
            [LOGS.GraphManager, LOGS.AnalysisLifecycle],
            `fetchMails (skip: ${skip}, top: ${top}) ... `,
        );

        let generalCopyRefs: MailCopyRef[] = [];

        try {
            // Construire l'URL de requête avec tous les paramètres
            // parentFolderId : identité durable de la copie (folderId Graph opaque), nécessaire pour le modèle multi-copies.
            const request = `/me/mailFolders/${encodeURIComponent(mailFolder)}/messages?$top=${top}&$skip=${skip}&$select=id,internetMessageId,parentFolderId`;
            const result = await this.callGraphEndPoint(request);

            if (!result || !result.value) {
                logger.error(
                    LOGS.GraphManager,
                    "fetchGeneralInternetMessageIds - No data returned from Graph API",
                );
                throw new Error(
                    "fetchGeneralInternetMessageIds - No data returned from Graph API",
                );
            }

            await this._fixTruncatedInternetMessageIds(
                result.value,
                "fetchGeneralInternetMessageIds",
            );

            // Construire les références de copie (id Graph + internetMessageId + parentFolderId).
            generalCopyRefs = result.value.map((message: any) => ({
                id: message.id,
                internetMessageId: message.internetMessageId,
                parentFolderId: message.parentFolderId,
            }));

            // Ajouter les informations de pagination à la réponse si disponibles
            if (result["@odata.nextLink"]) {
                logger.warn(
                    [LOGS.GraphManager, LOGS.AnalysisLifecycle],
                    `More results available at: ${result["@odata.nextLink"]}`,
                );
            }
        } catch (error) {
            logger.error(
                [LOGS.GraphManager, LOGS.AnalysisLifecycle],
                `Error in fetchGeneralInternetMessageIds: ${error}`,
            );
            throw error; // Propager l'erreur pour permettre une gestion appropriée par l'appelant
        }

        return generalCopyRefs;
    }

    /**
     * Les copies du dossier Envoyés qui répondent aux critères, `limit` au plus, triées par date
     * d'envoi (boîte « envoyes » de l'assistant). Un seul dossier : rien n'est cherché ailleurs.
     * Sans destinataire ni mot-clé, la liste du dossier (`$filter`, exacte) ; sinon une recherche par
     * terme (`$search`), filtrée ensuite ici (cf. sent-search-logic.ts). Une lecture impossible lève.
     */
    public async searchSentMailCopyRefs(
        criteria: SentMailsCriteria,
        limit: number,
    ): Promise<MailCopyRef[]> {
        logger.info([LOGS.GraphManager], `searchSentMailCopyRefs ...`);
        try {
            let items: any[];
            if (!hasSentSearchTerms(criteria)) {
                const response = await this.callGraphEndPoint(
                    sentFolderListUrl(criteria, limit),
                );
                if (!response?.value) {
                    throw new Error(
                        "searchSentMailCopyRefs - No data returned from Graph API",
                    );
                }
                items = response.value;
            } else {
                const urls = sentFolderSearchUrls(criteria);
                const all = [...urls.recipients, ...urls.keywords];
                // Un seul terme, sans filtre appliqué ensuite et du plus récent au plus ancien : la
                // première page suffit dès qu'elle atteint la limite.
                const firstPageIsEnough =
                    all.length === 1 &&
                    !criteria.startDate &&
                    !criteria.endDate &&
                    criteria.hasAttachment === undefined &&
                    criteria.sortOrder === "DESC";
                const results = await this._readSentSearch(
                    all,
                    (read, page) =>
                        (firstPageIsEnough && read.length >= limit) ||
                        isPastStart(page, criteria.startDate),
                );
                items = selectSentResults(
                    results.slice(0, urls.recipients.length).flat(),
                    results.slice(urls.recipients.length).flat(),
                    criteria,
                    limit,
                );
            }

            await this._fixTruncatedInternetMessageIds(
                items,
                "searchSentMailCopyRefs",
            );
            return items.map((message: any) => ({
                id: message.id,
                internetMessageId: message.internetMessageId,
                parentFolderId: message.parentFolderId,
            }));
        } catch (error) {
            logger.error(
                [LOGS.GraphManager],
                `Error in searchSentMailCopyRefs: ${error}`,
            );
            throw error;
        }
    }

    /**
     * Les résultats de chaque recherche (une URL par terme), dans l'ordre des URLs : les premières
     * pages partent en un lot, les suivantes se lisent une à une jusqu'à `SENT_SEARCH_MAX_PAGES`, ou
     * jusqu'à ce que `stop(lus, page)` dise que la suite est inutile.
     */
    private async _readSentSearch(
        urls: string[],
        stop: (read: any[], page: any[]) => boolean,
    ): Promise<any[][]> {
        const batch = await this.sendBatchRequest({
            requests: urls.map((url, index) => ({
                id: index.toString(),
                method: "GET",
                url,
            })),
        });
        const results: any[][] = [];
        for (let index = 0; index < urls.length; index++) {
            const body = (batch?.responses ?? []).find(
                (response: any) => response.id === index.toString(),
            )?.body;
            if (!Array.isArray(body?.value)) {
                // Une recherche sans réponse n'est pas une recherche vide.
                throw new Error(
                    `searchSentMailCopyRefs - recherche ${index} sans réponse de Graph`,
                );
            }
            let page: any[] = body.value;
            const read: any[] = [...page];
            let nextLink: string | undefined = body["@odata.nextLink"];
            for (
                let pages = 1;
                nextLink && pages < SENT_SEARCH_MAX_PAGES && !stop(read, page);
                pages++
            ) {
                const next = await this.callGraphEndPoint(
                    nextLink.replace(graphApiUrl, ""),
                );
                if (!Array.isArray(next?.value)) break;
                page = next.value;
                read.push(...page);
                nextLink = next["@odata.nextLink"];
            }
            results.push(read);
        }
        return results;
    }

    public async getGeneralNavBarContent(
        mailFolders: string[],
    ): Promise<NavItem[]> {
        logger.info(
            LOGS.GraphManager,
            `getGeneralNavBarContent for folders: ${mailFolders.join(", ")}`,
        );

        try {
            const navItems: NavItem[] = [];
            const MAX_CONCURRENT = 3;

            // Separate successful and failed requests
            const successfulResponses: any[] = [];
            const failedFolders: string[] = [];

            // Process by chunks of MAX_CONCURRENT folders
            for (let i = 0; i < mailFolders.length; i += MAX_CONCURRENT) {
                const chunk = mailFolders.slice(i, i + MAX_CONCURRENT);

                // Batch request for this chunk
                const batchRequests: BatchRequestItem[] = chunk.map(
                    (folder) => ({
                        id: folder,
                        method: "GET",
                        url: `/me/mailFolders/${folder}`,
                    }),
                );

                // Best-effort : `sendBatchRequest` lève si une sous-requête est en échec
                // définitif (ex. dossier 404/403). On ne fait pas tomber toute la nav bar :
                // on bascule le chunk entier en récupération individuelle (boucle ci-dessous),
                // qui isole le dossier fautif et conserve les autres.
                try {
                    const batchResponse = await this.sendBatchRequest({
                        requests: batchRequests,
                    });

                    if (batchResponse && batchResponse.responses) {
                        for (const response of batchResponse.responses) {
                            // Sous-réponse exploitable : on conserve le dossier
                            if (response.status === 200 && response.body) {
                                successfulResponses.push(response);
                            }
                            // Sous-réponse inexploitable : on retentera ce dossier individuellement
                            else {
                                failedFolders.push(response.id);
                            }
                        }
                    }
                } catch (error) {
                    logger.warn(
                        [LOGS.GraphManager],
                        `getGeneralNavBarContent: batch des dossiers [${chunk.join(", ")}] en échec, bascule en récupération individuelle (${error})`,
                    );
                    failedFolders.push(...chunk);
                }
            }

            // Retry failed requests individually
            for (const folder of failedFolders) {
                try {
                    const folderData = await this.callGraphEndPoint(
                        `/me/mailFolders/${folder}`,
                    );

                    // Create batch-format response for uniform processing
                    successfulResponses.push({
                        id: folder,
                        status: 200,
                        body: folderData,
                    });
                } catch (error) {
                    // Log error but continue with others
                    logger.error(
                        LOGS.GraphManager,
                        `Failed to retrieve folder ${folder} on retry`,
                        error,
                    );
                }
            }

            // Transform successful responses to NavItems
            for (const response of successfulResponses) {
                const folder = response.id;
                const folderData = response.body;

                const navItem: NavItem = {
                    id: folder,
                    itemCount: folderData.unreadItemCount,
                    menuInfo: {
                        color: "#64748B",
                        icon: "",
                        title: folderData.displayName || folder,
                    },
                    isFavorite: false,
                };

                if (folder === MailFolder.Inbox) {
                    this.eventBus.emit(
                        "inbox-folder-id-updated",
                        folderData.id,
                    );
                } else {
                    this.eventBus.emit("mail-folder-id-updated", {
                        folderName: folder,
                        folderId: folderData.id,
                    });
                }

                navItems.push(navItem);
            }

            // Reorder according to original order
            const orderedNavItems: NavItem[] = [];
            for (const folder of mailFolders) {
                const navItem = navItems.find((item) => item.id === folder);
                if (navItem) {
                    orderedNavItems.push(navItem);
                }
            }

            return orderedNavItems;
        } catch (error) {
            logger.error(
                [LOGS.GraphManager],
                `GraphManager - Error in getGeneralNavBarContent : ${error}`,
            );
            throw error;
        }
    }

    /**
     * Get all mail internetMessageIds corresponding to a conversation ID
     * ═══════════════════════════════════════════════════════
     *
     * @param conversationId - ID of the conversation to fetch mails from
     * @returns Promise with array of mail internetMessageIds
     */
    public async getMailThreadInternetMessageIds(
        conversationId: string,
    ): Promise<Message[]> {
        logger.info(
            [LOGS.GraphManager, LOGS.AnalysisLifecycle],
            `Getting mail by conversationId ...`,
        );

        let response: any;
        try {
            // URLSearchParams : encodage délégué à la pile, sans %XX manuel. NB : la forme diffère entre web (`+`) et RN (`%20`) — Graph accepte les deux sur un appel direct ; non validé pour les sous-URLs $batch.
            // La valeur est seulement échappée OData (apostrophes doublées), JAMAIS encodeURIComponent.
            const threadParams = new URLSearchParams();
            threadParams.append("$select", "id,internetMessageId");
            threadParams.append(
                "$filter",
                `conversationId eq '${escapeODataStringValue(conversationId)}'`,
            );
            threadParams.append("$top", "100");
            response = await this.callGraphEndPoint(
                `/me/messages?${threadParams.toString()}`,
            );

            if (response.error) {
                logger.error(
                    LOGS.GraphManager,
                    `Error in getMailThread: ${response.error.message}`,
                );
                return [];
            }

            logger.info(LOGS.GraphManager, "MailThread content", response);

            const messages = response.value as Message[];

            await this._fixTruncatedInternetMessageIds(
                messages,
                "getMailThreadInternetMessageIds",
            );

            return messages;
        } catch (error) {
            logger.error(
                [LOGS.GraphManager],
                `Error in getMailThreadInternetMessageIds: ${error}`,
            );
            if (error.details) {
                logger.info(
                    [LOGS.GraphManager],
                    "getMailThreadInternetMessageIdsError",
                    error.details,
                );
            }
            throw error;
        }
    }

    /**
     * Fetches parent folder IDs for multiple mails from Microsoft Graph API in batches.
     * Uses exponential backoff for rate limit handling and processes requests sequentially with delays.
     *
     * @param internetMessageIds - Array of mail internet message IDs to lookup
     * @returns Record mapping internet message IDs to their parent folder IDs
     */
    public async getMailsParentFolderIds(
        internetMessageIds: string[],
    ): Promise<Record<string, string>> {
        logger.info(
            [LOGS.GraphManager],
            `log getMailsParentFolderIds for ${internetMessageIds.length} internetMessageIds`,
        );

        try {
            const batches = internetMessageIds.reduce((acc, id, index) => {
                const batchIndex = Math.floor(index / BATCH_SIZE);
                if (!acc[batchIndex]) {
                    acc[batchIndex] = [];
                }
                acc[batchIndex].push(id);
                return acc;
            }, [] as string[][]);

            const fetchedParentFolder = await batches.reduce(
                async (previousPromise, batch) => {
                    const accumulatedResults = await previousPromise;

                    const filterConditions = batch
                        .map(
                            (internetMessageId) =>
                                `internetMessageId eq '${escapeODataStringValue(internetMessageId)}'`,
                        )
                        .join(" or ");

                    // Sélectionner uniquement les champs nécessaires.
                    // URLSearchParams : encodage délégué à la pile, sans %XX manuel. NB : la forme diffère entre web (`+`) et RN (`%20`) — Graph accepte les deux sur un appel direct ; non validé pour les sous-URLs $batch.
                    //
                    // les valeurs ne sont qu'échappées OData, pas encodeURIComponent.
                    const parentFolderParams = new URLSearchParams();
                    parentFolderParams.append("$top", `${batch.length}`);
                    parentFolderParams.append("$filter", filterConditions);
                    parentFolderParams.append(
                        "$select",
                        "internetMessageId,parentFolderId",
                    );
                    const query = `/me/messages?${parentFolderParams.toString()}`;

                    // `callGraphEndPoint` gère déjà le retry 429 (Retry-After + backoff)
                    // et lève une GraphThrottleError typée après épuisement — pas besoin
                    // d'un wrapper exponentialBackoff redondant (détection par string supprimée).
                    const batchResult = await this.callGraphEndPoint(query);

                    if (batchResult?.value) {
                        batchResult.value.forEach((msg: any) => {
                            accumulatedResults[msg.internetMessageId] =
                                msg.parentFolderId;
                        });
                    }

                    await new Promise((resolve) =>
                        setTimeout(resolve, BATCH_DELAY),
                    );

                    return accumulatedResults;
                },
                Promise.resolve({} as Record<string, string>),
            );

            return fetchedParentFolder;
        } catch (error) {
            const textError = `getMailsParentFolderIds - Error getting parent folder for mail: ${error.message}`;
            logger.error([LOGS.GraphManager], textError);
            throw new Error(textError);
        }
    }

    //#endregion Getters

    // #region User actions

    public async deleteMail(mailId: string): Promise<void> {
        try {
            const deleteRequest = `/me/messages/${encodeURIComponent(mailId)}`;
            await this.callGraphEndPoint(deleteRequest, "DELETE", {});
        } catch (error) {
            logger.error(
                [LOGS.GraphManager],
                `Error deleting mail ${mailId}: ${error}`,
            );
            // AB#1764 — préserver le status HTTP (404 → suppression idempotente en amont).
            throw inheritHttpStatus(
                new Error(`Error deleting mail ${mailId}: ${error}`),
                error,
            ); // Relance l'erreur pour qu'elle soit gérée par l'appelant
        }
    }

    public async sendMail(mailData: MailSend): Promise<SendMailResult> {
        logger.info(
            [LOGS.GraphManager, LOGS.AnalysisLifecycle],
            `sendMail to ${mailData.toRecipients.join(", ")}...`,
        );

        try {
            // First step is to save mail in the Drafts folder
            const message = {
                subject: mailData.subject,
                body: {
                    contentType: mailData.contentType || "Text",
                    content: mailData.content,
                },
                toRecipients: mailData.toRecipients?.map((recipient) => ({
                    emailAddress: {
                        address: recipient,
                    },
                })),
                ccRecipients: mailData.ccRecipients?.map((recipient) => ({
                    emailAddress: {
                        address: recipient,
                    },
                })),
                bccRecipients: mailData.bccRecipients?.map((recipient) => ({
                    emailAddress: {
                        address: recipient,
                    },
                })),
                attachments: mailData.attachments?.map((attachment) => {
                    const graphAttachment: any = {
                        "@odata.type": "#microsoft.graph.fileAttachment",
                        name: attachment.name,
                        contentType: attachment.contentType,
                        contentBytes: attachment.contentBytes,
                    };

                    // Add inline-specific properties if present
                    if (attachment.isInline) {
                        graphAttachment.isInline = true;
                        graphAttachment.contentId = attachment.contentId;
                    }

                    return graphAttachment;
                }),
            };

            const draftResponse = await this.callGraphEndPoint(
                "/me/messages",
                "POST",
                message,
                false,
                true,
            );
            if (draftResponse?.error) {
                logger.error(
                    [LOGS.GraphManager],
                    `Error sending mail: ${draftResponse?.error.message}`,
                );
                throw new Error(
                    `Error sending mail: ${draftResponse?.error.message}`,
                );
            }
            const responseId = draftResponse.id;
            // internetMessageId du brouillon : marqueur UNIQUE et stable (brouillon→Sent) du
            // message qu'on vient d'envoyer → identifie LA bonne copie Sent (l'id du brouillon
            // devient périmé après le move). Cf. #1332.
            const internetMessageId: string | undefined =
                draftResponse.internetMessageId;
            logger.info(
                [LOGS.GraphManager],
                `  Draft created with ID: ${responseId}`,
            );

            // Then if draft creation succeeded, send the mail and emit events
            await this.callGraphEndPoint(
                `/me/messages/${responseId}/send`,
                "POST",
                null,
            );

            // L'envoi déplace le message Drafts → Sent Items et CHANGE son id Graph → l'id du
            // brouillon est périmé. On rend la main immédiatement ; la copie Sent (id valide) est
            // re-résolue EN ARRIÈRE-PLAN par internetMessageId. Émission 'mail-sent' centralisée (#4).
            const deferredMailSentId = internetMessageId
                ? this._resolveSentMailId(internetMessageId, responseId)
                : Promise.resolve(responseId);
            return { mailSentId: null, deferredMailSentId };
        } catch (error) {
            logger.error(
                [LOGS.GraphManager, LOGS.AnalysisLifecycle],
                `  !!! Error in sendMail: ${error}`,
            );
            throw new Error(`  !!! Error in sendMail: ${error}`);
        }
    }

    public async replyToMail(
        messageId: string,
        replyData: ReplyForward,
        replyAll: boolean = false,
    ): Promise<SendMailResult> {
        const replyType = replyAll ? "replyAll" : "reply";
        const createReplyType = replyAll ? "createReplyAll" : "createReply";
        logger.info(
            [LOGS.GraphManager, LOGS.AnalysisLifecycle],
            `${replyType} to message ${messageId}...`,
        );

        try {
            // First step is to save mail in the Drafts folder
            const replyMessage = {
                comment: replyData.content,
                // TODO - Map to user current language
                // contentLanguage: "fr-FR",
                message: {
                    toRecipients: replyData.toRecipients?.map((recipient) => ({
                        emailAddress: {
                            address: recipient,
                        },
                    })),
                    ccRecipients: replyData.ccRecipients?.map((recipient) => ({
                        emailAddress: {
                            address: recipient,
                        },
                    })),
                    bccRecipients: replyData.bccRecipients?.map(
                        (recipient) => ({
                            emailAddress: {
                                address: recipient,
                            },
                        }),
                    ),
                    attachments: replyData.attachments?.map((attachment) => {
                        const graphAttachment: any = {
                            "@odata.type": "#microsoft.graph.fileAttachment",
                            name: attachment.name,
                            contentType: attachment.contentType,
                            contentBytes: attachment.contentBytes,
                        };

                        // Add inline-specific properties if present
                        if (attachment.isInline) {
                            graphAttachment.isInline = true;
                            graphAttachment.contentId = attachment.contentId;
                        }

                        return graphAttachment;
                    }),
                },
            };

            const draftResponse = await this.callGraphEndPoint(
                `/me/messages/${encodeURIComponent(messageId)}/${createReplyType}`,
                "POST",
                replyMessage,
                false,
                true,
            );
            if (draftResponse?.error) {
                logger.error(
                    [LOGS.GraphManager],
                    `Error creating reply draft: ${draftResponse?.error.message}`,
                );
                throw new Error(
                    `Error creating reply draft: ${draftResponse?.error.message}`,
                );
            }
            const responseId = draftResponse.id;
            const internetMessageId: string | undefined =
                draftResponse.internetMessageId;
            logger.info(
                [LOGS.GraphManager],
                `  Reply draft created with ID: ${responseId}`,
            );

            // Then if draft creation succeeded, send the mail and emit events
            await this.callGraphEndPoint(
                `/me/messages/${responseId}/send`,
                "POST",
                null,
            );

            // Résolution DIFFÉRÉE de la copie Sent par internetMessageId (marqueur unique — fiable
            // même dans un fil existant) ; emit 'mail-sent' centralisé dans DataManager (#4). Cf. #1332.
            const deferredMailSentId = internetMessageId
                ? this._resolveSentMailId(internetMessageId, responseId)
                : Promise.resolve(responseId);
            return { mailSentId: null, deferredMailSentId };
        } catch (error) {
            logger.error(
                [LOGS.GraphManager, LOGS.AnalysisLifecycle],
                `  !!! Error in replyToMail: ${error}`,
            );
            throw new Error(`  !!! Error in replyToMail: ${error}`);
        }
    }

    public async resolveSentMailId(
        internetMessageId: string,
    ): Promise<string | null> {
        return this._resolveSentMailIdByInternetMessageId(internetMessageId);
    }

    /**
     * Re-résout l'id de la copie Sent après /send (l'id du brouillon est périmé : move Drafts→Sent).
     * Renvoie l'id Sent valide, ou l'id du brouillon en repli. N'émet pas (#4 : emit centralisé
     * dans DataManager). Détail : Doc/identite-mail/18_pieces-jointes-1332.md.
     */
    private async _resolveSentMailId(
        internetMessageId: string,
        fallbackDraftId: string,
    ): Promise<string> {
        try {
            const sentId =
                await this._resolveSentMailIdByInternetMessageId(
                    internetMessageId,
                );
            if (sentId) {
                logger.info(
                    [LOGS.GraphManager],
                    `mail-sent: copie Sent résolue (${sentId}) pour internetMessageId ${internetMessageId}`,
                );
                return sentId;
            }
            logger.warn(
                [LOGS.GraphManager],
                `mail-sent: copie Sent introuvable pour internetMessageId ${internetMessageId} après retries — repli sur l'id du brouillon`,
            );
        } catch (error) {
            logger.error(
                [LOGS.GraphManager],
                `mail-sent: erreur de re-résolution de la copie Sent (internetMessageId ${internetMessageId}): ${error} — repli sur l'id du brouillon`,
            );
        }
        return fallbackDraftId;
    }

    /**
     * Cherche dans Sent Items LA copie du message qu'on vient d'envoyer, par son `internetMessageId`
     * (marqueur UNIQUE → fiable même dans un fil existant, contrairement à « la plus récente de la
     * conversation » qui renverrait une copie antérieure tant que la fraîche n'est pas indexée).
     * Polling borné (envoi Exchange asynchrone). Renvoie l'id par défaut ou null.
     */
    private async _resolveSentMailIdByInternetMessageId(
        internetMessageId: string,
    ): Promise<string | null> {
        // URLSearchParams : encodage délégué à la pile, sans %XX manuel. NB : la forme diffère entre web (`+`) et RN (`%20`) — Graph accepte les deux sur un appel direct ; non validé pour les sous-URLs $batch. Échappement OData seul sur les valeurs.
        const params = new URLSearchParams();
        params.append(
            "$filter",
            `internetMessageId eq '${escapeODataStringValue(internetMessageId)}'`,
        );
        params.append("$top", "1");
        params.append("$select", "id,internetMessageId,parentFolderId");
        const query = `/me/mailFolders/sentitems/messages?${params.toString()}`;

        // Le filtre cible EXACTEMENT notre message → 0 (pas encore indexé) ou 1 copie.
        // `_pollUntil` (délais ~5.6s cumulés) absorbe le lag d'envoi asynchrone Exchange.
        return this._pollUntil(async () => {
            const response = await this.callGraphEndPoint(query);
            return response?.value?.[0]?.id ?? null;
        }, [600, 1000, 1500, 2500]);
    }

    /**
     * Réessaie `operation` jusqu'à un résultat non-null, avec attente entre chaque passe
     * (absorbe une latence async serveur). `delaysMs.length` = nombre de retries.
     */
    private async _pollUntil<T>(
        operation: () => Promise<T | null>,
        delaysMs: number[],
    ): Promise<T | null> {
        for (let attempt = 0; attempt <= delaysMs.length; attempt++) {
            const result = await operation();
            if (result !== null) return result;
            if (attempt < delaysMs.length) {
                await new Promise((resolve) =>
                    setTimeout(resolve, delaysMs[attempt]),
                );
            }
        }
        return null;
    }

    public async setReadStatus(
        messageId: string,
        isRead: boolean,
    ): Promise<boolean> {
        logger.info(
            [LOGS.GraphManager, LOGS.AnalysisLifecycle],
            `Setting read status to ${isRead} for message ${messageId}...`,
        );

        try {
            await this.callGraphEndPoint(
                `/me/messages/${encodeURIComponent(messageId)}`,
                "PATCH",
                {
                    isRead: isRead,
                },
            );
            logger.info(
                [LOGS.GraphManager, LOGS.AnalysisLifecycle],
                "Read status updated successfully",
            );
            return true;
        } catch (error) {
            logger.error(
                [LOGS.GraphManager, LOGS.AnalysisLifecycle],
                `Error in setReadStatus: ${error}`,
            );
            return false;
        }
    }

    /**
     * Updates `isRead` on multiple messages in one HTTP round-trip using the
     * Graph JSON Batching endpoint (`POST /$batch`).
     *
     * Each chunk of up to 20 items is sent as a single batch request containing
     * individual PATCH sub-requests. Per-item status codes in the response are
     * classified as:
     *   • 2xx        → success
     *   • 429 / 5xx  → retryable (throttle or transient server error)
     *   • other 4xx  → definitive failure (bad request, not found…)
     *
     * Retry strategy — exponential backoff, applied per chunk:
     *   attempt 1  →  immediate
     *   attempt 2  →  wait  1 000 ms
     *   attempt 3  →  wait  2 000 ms   (cap: 16 000 ms)
     *
     * Items still failing after `maxRetries` are placed in `result.failed`.
     *
     * @param items      - {messageId, isRead} pairs to update (Graph message IDs)
     * @param maxRetries - Maximum attempts per 20-item chunk (default 3)
     * @returns success: Graph IDs updated, failed: IDs that could not be updated
     */
    public async batchSetReadStatus(
        items: BatchReadStatusItem[],
        maxRetries = 3,
    ): Promise<BatchReadStatusResult> {
        const GRAPH_BATCH_MAX = 20; // Graph $batch hard limit per request
        const BATCH_DELAY_MS = 500; // Pause between consecutive chunks
        const result: BatchReadStatusResult = { success: [], failed: [] };

        for (let i = 0; i < items.length; i += GRAPH_BATCH_MAX) {
            let chunk = items.slice(i, i + GRAPH_BATCH_MAX);
            let attempt = 0;
            // Headers of the most recent 429 sub-response → Retry-After-driven delay.
            let lastThrottleHeaders:
                | GraphBatchSubResponse["headers"]
                | undefined;

            // Retry loop: `chunk` shrinks each pass — only retryable items are kept
            while (chunk.length > 0 && attempt < maxRetries) {
                if (attempt > 0) {
                    // Shared helper: honors sub-response Retry-After, else exponential backoff + jitter (cap 16s).
                    const retryDelay = computeThrottleDelayMs(
                        lastThrottleHeaders,
                        attempt,
                        { maxDelayMs: 16000 },
                    );
                    logger.info(
                        [LOGS.GraphManager],
                        `batchSetReadStatus: retry ${attempt}/${maxRetries - 1} — ${chunk.length} item(s), waiting ${retryDelay}ms`,
                    );
                    await new Promise((resolve) =>
                        setTimeout(resolve, retryDelay),
                    );
                }

                // One PATCH sub-request per item; `id` maps responses back to items
                const batchPayload = {
                    requests: chunk.map((item, idx) => ({
                        id: String(idx),
                        method: "PATCH",
                        url: `/me/messages/${encodeURIComponent(item.messageId)}`,
                        headers: { "Content-Type": "application/json" },
                        body: { isRead: item.isRead },
                    })),
                };

                logger.info(
                    [LOGS.GraphManager],
                    `batchSetReadStatus: attempt ${attempt + 1}, sending ${chunk.length} request(s)`,
                );
                const batchResponse = await this.callGraphEndPoint(
                    "/$batch",
                    "POST",
                    batchPayload,
                );
                const retryable: BatchReadStatusItem[] = [];
                lastThrottleHeaders = undefined;

                // Classify each individual sub-response
                batchResponse.responses.forEach(
                    (resp: GraphBatchSubResponse) => {
                        const item = chunk[parseInt(resp.id, 10)];
                        if (resp.status >= 200 && resp.status < 300) {
                            result.success.push(item.messageId);
                        } else if (resp.status === 429 || resp.status >= 500) {
                            // Throttled or transient server error → retry next iteration
                            retryable.push(item);
                            if (resp.status === 429 && resp.headers) {
                                lastThrottleHeaders = resp.headers;
                            }
                        } else {
                            // Other 4xx: definitive failure, no point retrying
                            logger.warn(
                                [LOGS.GraphManager],
                                `batchSetReadStatus: ${item.messageId} → HTTP ${resp.status} (non-retryable)`,
                            );
                            result.failed.push(item.messageId);
                        }
                    },
                );

                chunk = retryable;
                attempt++;
            }

            // Items remaining in `chunk` exhausted all retry attempts
            for (const item of chunk) {
                logger.error(
                    [LOGS.GraphManager],
                    `batchSetReadStatus: ${item.messageId} failed after ${maxRetries} attempt(s)`,
                );
                result.failed.push(item.messageId);
            }

            // Brief pause before the next chunk to reduce burst pressure on Graph
            if (i + GRAPH_BATCH_MAX < items.length) {
                await new Promise((resolve) =>
                    setTimeout(resolve, BATCH_DELAY_MS),
                );
            }
        }

        return result;
    }

    public async setImportance(
        messageId: string,
        importance: MailImportance,
    ): Promise<boolean> {
        logger.info(
            [LOGS.GraphManager, LOGS.AnalysisLifecycle],
            `Setting importance to ${importance} for message ${messageId}...`,
        );

        try {
            await this.callGraphEndPoint(
                `/me/messages/${encodeURIComponent(messageId)}`,
                "PATCH",
                {
                    importance: importance,
                },
            );
            logger.info(
                [LOGS.GraphManager, LOGS.AnalysisLifecycle],
                "Importance updated successfully",
            );
            return true;
        } catch (error) {
            logger.error(
                [LOGS.GraphManager, LOGS.AnalysisLifecycle],
                `Error in setImportance: ${error}`,
            );
            return false;
        }
    }

    public async setFlag(
        messageId: string,
        flag: { flagStatus: MailFlagStatus } | null,
    ): Promise<boolean> {
        logger.info(
            [LOGS.GraphManager, LOGS.AnalysisLifecycle],
            `Setting flag status to ${flag} for message ${messageId}...`,
        );

        try {
            await this.callGraphEndPoint(
                `/me/messages/${encodeURIComponent(messageId)}`,
                "PATCH",
                {
                    flag: flag,
                },
            );

            logger.info(
                [LOGS.GraphManager, LOGS.AnalysisLifecycle],
                "Flag status updated successfully",
            );
            return true;
        } catch (error) {
            logger.error(
                [LOGS.GraphManager, LOGS.AnalysisLifecycle],
                `Error in setFlag: ${error}`,
            );
            return false;
        }
    }

    /**
     * Supprime un lot d'emails via Graph JSON Batching ($batch)
     * ═══════════════════════════════════════════════════════════
     *
     * Envoie les opérations en chunks de 20 (limite Graph) pour éviter les 429
     * liés à des appels parallèles. Chaque item peut être soit une suppression
     * définitive (DELETE → 204) soit un déplacement vers « Éléments supprimés »
     * (POST /move → 201 + nouveau Graph ID dans le body).
     *
     * Stratégie de retry par chunk :
     * ┌──────────┬────────────────────────────────────────────────────────────────┐
     * │ HTTP     │ Traitement                                                     │
     * ├──────────┼────────────────────────────────────────────────────────────────┤
     * │ 204      │ isPermanent=true  → result.permanentDeleted                    │
     * │ 201      │ isPermanent=false → result.moved (newMessageId depuis body)    │
     * │ 404      │ Déjà supprimé côté messagerie → result.notFound (idempotent)   │
     * │ 429/5xx  │ Retryable — exponential backoff (1 s → 2 s → 4 s, max 16 s)  │
     * │ Autres   │ Échec définitif → result.failed (loggé en warn)               │
     * └──────────┴────────────────────────────────────────────────────────────────┘
     *
     * ⚠️  Graph réassigne l'ID du message après un /move — le `newMessageId`
     *     retourné dans `result.moved` doit être utilisé pour mettre à jour
     *     le Record local (cf. `_batchDeleteMails` dans MailManager).
     *
     * @param items       - Liste des items à supprimer (messageId Graph + isPermanent)
     * @param maxRetries  - Nombre maximum de tentatives par chunk (défaut : 3)
     * @returns           - `permanentDeleted` : IDs définitivement supprimés
     *                    - `moved`           : IDs déplacés avec leur nouvel ID Graph
     *                    - `notFound`        : IDs déjà absents côté messagerie (404, idempotent)
     *                    - `failed`          : IDs en échec définitif
     */
    public async batchDeleteMails(
        items: BatchDeleteItem[],
        maxRetries = 3,
    ): Promise<BatchDeleteResult> {
        const GRAPH_BATCH_MAX = 20;
        const BATCH_DELAY_MS = 500;
        const result: BatchDeleteResult = {
            permanentDeleted: [],
            moved: [],
            notFound: [],
            failed: [],
        };

        for (let i = 0; i < items.length; i += GRAPH_BATCH_MAX) {
            let chunk = items.slice(i, i + GRAPH_BATCH_MAX);
            let attempt = 0;
            // Headers de la sous-réponse 429 la plus récente → délai piloté par Retry-After.
            let lastThrottleHeaders:
                | GraphBatchSubResponse["headers"]
                | undefined;

            while (chunk.length > 0 && attempt < maxRetries) {
                if (attempt > 0) {
                    // Helper partagé : Retry-After (sous-réponse) sinon backoff exponentiel + jitter (cap 16s).
                    const retryDelay = computeThrottleDelayMs(
                        lastThrottleHeaders,
                        attempt,
                        { maxDelayMs: 16000 },
                    );
                    logger.info(
                        [LOGS.GraphManager],
                        `batchDeleteMails: retry ${attempt}/${maxRetries - 1} — ${chunk.length} item(s), waiting ${retryDelay}ms`,
                    );
                    await new Promise((resolve) =>
                        setTimeout(resolve, retryDelay),
                    );
                }

                const batchPayload = {
                    requests: chunk.map((item, idx) =>
                        item.isPermanent
                            ? {
                                  id: String(idx),
                                  method: "DELETE",
                                  url: `/me/messages/${encodeURIComponent(item.messageId)}`,
                              }
                            : {
                                  id: String(idx),
                                  method: "POST",
                                  url: `/me/messages/${encodeURIComponent(item.messageId)}/move`,
                                  headers: {
                                      "Content-Type": "application/json",
                                  },
                                  body: { destinationId: "deleteditems" },
                              },
                    ),
                };

                logger.info(
                    [LOGS.GraphManager],
                    `batchDeleteMails: attempt ${attempt + 1}, sending ${chunk.length} request(s)`,
                );
                const batchResponse = await this.callGraphEndPoint(
                    "/$batch",
                    "POST",
                    batchPayload,
                );
                const retryable: BatchDeleteItem[] = [];
                lastThrottleHeaders = undefined;

                batchResponse.responses.forEach(
                    (resp: GraphBatchSubResponse) => {
                        const item = chunk[parseInt(resp.id, 10)];
                        if (item.isPermanent && resp.status === 204) {
                            result.permanentDeleted.push(item.messageId);
                        } else if (!item.isPermanent && resp.status === 201) {
                            const newId = resp.body?.id;
                            if (!newId) {
                                logger.warn(
                                    [LOGS.GraphManager],
                                    `batchDeleteMails: moved ${item.messageId} but no new ID in response`,
                                );
                            }
                            result.moved.push({
                                messageId: item.messageId,
                                newMessageId: newId ?? item.messageId,
                            });
                        } else if (resp.status === 404) {
                            // AB#1764 — copie déjà supprimée côté messagerie (Outlook / hors MailVista).
                            // Suppression idempotente : ni retry, ni échec → bucket dédié `notFound`.
                            logger.warn(
                                [LOGS.GraphManager],
                                `batchDeleteMails: ${item.messageId} → HTTP 404 (déjà supprimé, idempotent)`,
                            );
                            result.notFound.push(item.messageId);
                        } else if (resp.status === 429 || resp.status >= 500) {
                            retryable.push(item);
                            if (resp.status === 429 && resp.headers) {
                                lastThrottleHeaders = resp.headers;
                            }
                        } else {
                            logger.warn(
                                [LOGS.GraphManager],
                                `batchDeleteMails: ${item.messageId} → HTTP ${resp.status} (non-retryable)`,
                            );
                            result.failed.push(item.messageId);
                        }
                    },
                );

                chunk = retryable;
                attempt++;
            }

            for (const item of chunk) {
                logger.error(
                    [LOGS.GraphManager],
                    `batchDeleteMails: ${item.messageId} failed after ${maxRetries} attempt(s)`,
                );
                result.failed.push(item.messageId);
            }

            if (i + GRAPH_BATCH_MAX < items.length) {
                await new Promise((resolve) =>
                    setTimeout(resolve, BATCH_DELAY_MS),
                );
            }
        }

        return result;
    }

    /**
     * Déplace un email vers un dossier spécifique
     * ═══════════════════════════════════════════════════════════
     *
     * @param mailId - ID de l'email à déplacer
     * @param destinationId - ID du dossier de destination (ex: 'deleteditems', 'inbox', 'drafts', etc.)
     * @returns Promise<any> - return response id of mail moved
     */
    public async moveMail(
        mailId: string,
        destinationId: string,
    ): Promise<Message> {
        logger.info(
            [LOGS.GraphManager],
            `Moving mail ${mailId} to ${destinationId}...`,
        );

        try {
            const moveRequest = `/me/messages/${encodeURIComponent(mailId)}/move`;
            const requestBody = {
                destinationId: destinationId,
            };

            const response = await this.callGraphEndPoint(
                moveRequest,
                "POST",
                requestBody,
            );

            if (response?.error) {
                logger.error(
                    [LOGS.GraphManager],
                    `Error moving mail: ${response.error.message}`,
                );
                throw new Error(`Error moving mail: ${response.error.message}`);
            }

            const result = response as Message;

            return result;
        } catch (error) {
            logger.error([LOGS.GraphManager], `Error in moveMail: ${error}`);
            // AB#1764 — préserver le status HTTP (404 → suppression idempotente en amont).
            throw inheritHttpStatus(
                new Error(`Error moving mail: ${error}`),
                error,
            );
        }
    }

    // #endregion User actions

    // #region Private Method

    /**
     * Fixes truncated internetMessageId values by fetching complete IDs from Microsoft Graph API.
     * Some Graph API responses truncate internetMessageId fields (missing trailing '>').
     * This method identifies and repairs those truncated values with individual API calls.
     *
     * The fix is done directly in reference received, no need to return fixed array
     *
     * @param messages - Array of messages to check and fix
     * @param contextLog - Optional logging prefix for tracking the operation context
     * @returns Promise resolving to the messages array with fixed internetMessageId values
     */
    private async _fixTruncatedInternetMessageIds(
        messages: Array<{
            id?: string | null;
            internetMessageId?: string | null;
        }>,
        contextLog: string = "",
    ): Promise<void> {
        // Filter messages with truncated internetMessageId (missing closing '>')
        const truncatedMessages = messages.filter(
            (m) => m.internetMessageId && !m.internetMessageId.endsWith(">"),
        );

        if (truncatedMessages.length > 0) {
            logger.info(
                [LOGS.GraphManager],
                `${contextLog} - Found ${truncatedMessages.length} truncated internetMessageId(s), fixing...`,
            );

            // Fetch complete internetMessageId for each truncated message
            for (const message of truncatedMessages) {
                try {
                    const fullMessage = await this.callGraphEndPoint(
                        `/me/messages/${encodeURIComponent(message.id!)}?$select=internetMessageId`,
                    );

                    if (fullMessage?.internetMessageId) {
                        message.internetMessageId =
                            fullMessage.internetMessageId;
                    }
                } catch (error) {
                    logger.error(
                        [LOGS.GraphManager],
                        `${contextLog} - Failed to fix truncated internetMessageId(s) for message ${message.id}: ${error}`,
                    );
                }
            }
        }
    }

    // #endregion Private Method

    // #endregion Mails

    // #region Agenda
    public async getUserAgenda(filter: AgendaFilter): Promise<Event[]> {
        logger.info(
            LOGS.GraphManager,
            `getUserAgenda with filter: ${JSON.stringify(filter)}`,
        );

        // Vérification des paramètres obligatoires
        if (!filter.startDate || !filter.endDate) {
            throw new Error(
                "startDate and endDate are required in AgendaFilter",
            );
        }

        // Valeurs par défaut pour les propriétés optionnelles
        const attendees = filter.attendees || [];
        const limit = filter.limit || 1000;
        const sortDirection = filter.orderBy || CustomViewSortOrder.ASC;
        const keywords = filter.keywords || [];

        let response: any;
        try {
            // Construction de la requête via URLSearchParams : encodage de transport
            // sans %XX manuel ni espace littéral ; forme web/RN non identique.
            const agendaParams = new URLSearchParams();
            agendaParams.append("startDateTime", filter.startDate);
            agendaParams.append("endDateTime", filter.endDate);
            // `responseStatus` : réponse de l'utilisateur ; une réunion refusée ne bloque pas ses
            // heures dans les créneaux libres (F-1.12).
            agendaParams.append(
                "$select",
                "subject,bodyPreview,body,start,end,location,attendees,organizer,categories,onlineMeeting,isAllDay,isCancelled,importance,sensitivity,showAs,type,webLink,createdDateTime,lastModifiedDateTime,responseStatus",
            );
            agendaParams.append("$orderby", `start/dateTime ${sortDirection}`);
            agendaParams.append(
                "$top",
                attendees.length > 0 ? "100" : `${limit}`,
            );

            // Filtrage par mots-clés (si présents) - uniquement sur le subject.
            // Valeurs échappées OData (apostrophes doublées) ; jamais encodeURIComponent.
            if (keywords.length > 0) {
                const keywordFilter = keywords
                    .map(
                        (keyword) =>
                            `contains(subject,'${escapeODataStringValue(keyword)}')`,
                    )
                    .join(" or ");
                agendaParams.append("$filter", keywordFilter);
            }

            const request = `/me/calendarView?${agendaParams.toString()}`;

            response = await this.callGraphEndPoint(request);

            logger.info(LOGS.GraphManager, "UserAgenda", response.value);

            let result = response.value as Event[];

            // Filtrage par participants (côté client car Graph API ne supporte pas facilement ce filtre)
            if (attendees.length > 0) {
                result = result.filter((event) => {
                    if (!event.attendees) return false;

                    return attendees.some(
                        (filterAttendee) =>
                            event.attendees!.some(
                                (eventAttendee) =>
                                    eventAttendee.emailAddress?.address
                                        ?.toLowerCase()
                                        .includes(
                                            filterAttendee.toLowerCase(),
                                        ) ||
                                    eventAttendee.emailAddress?.name
                                        ?.toLowerCase()
                                        .includes(filterAttendee.toLowerCase()),
                            ) ||
                            event.organizer?.emailAddress?.address?.toLocaleLowerCase() ===
                                filterAttendee.toLowerCase(),
                    );
                });

                if (filter.limit) {
                    result = result.slice(0, filter.limit);
                }
            }

            return result;
        } catch (error) {
            if (error instanceof GraphThrottleError) throw error; // préserve le type 429
            logger.error(
                LOGS.GraphManager,
                `Error in getUserAgenda: ${error.message}`,
            );
            throw new Error(`Error in getUserAgenda: ${error.message}`);
        }
    }

    /**
     * Paginated calendarView retrieval (AB#158).
     * Honors filter.startDate/endDate as the window, `$orderby=start/dateTime`, `$top=limit`
     * (page size), and follows the opaque `@odata.nextLink` cursor. `options.includeBody=false`
     * uses a light `$select` (no `body`) for cheap scans — the body is hydrated separately for
     * the displayed items via getAgendaItemBodies. Attendee filtering stays client-side (in the
     * AgendaManager), as Graph cannot filter a collection like `attendees` server-side.
     */
    public async getUserAgendaPage(
        filter: AgendaFilter,
        cursor?: string,
        options?: { includeBody?: boolean },
    ): Promise<AgendaEventsPage> {
        logger.info(
            LOGS.GraphManager,
            `getUserAgendaPage(cursor: ${cursor ? "yes" : "no"}, includeBody: ${options?.includeBody ?? true})`,
        );

        try {
            let response: any;

            if (cursor) {
                // Follow the opaque @odata.nextLink (relative form, like the mail pagination).
                // The nextLink already encodes $select/$orderby/$filter of the initial request.
                const relativeUrl = cursor.startsWith(graphApiUrl)
                    ? cursor.replace(graphApiUrl, "")
                    : cursor;
                response = await this.callGraphEndPoint(relativeUrl);
            } else {
                if (!filter.startDate || !filter.endDate) {
                    throw new Error(
                        "startDate and endDate are required in AgendaFilter",
                    );
                }

                const pageSize = filter.limit || 25;
                const sortDirection = filter.orderBy || CustomViewSortOrder.ASC;
                const keywords = filter.keywords || [];

                // Light projection (no `body`) when includeBody=false → cheap dense scans.
                const selectFields =
                    (options?.includeBody ?? true)
                        ? "subject,bodyPreview,body,start,end,location,attendees,organizer,categories,onlineMeeting,isAllDay,isCancelled,importance,sensitivity,showAs,type,webLink,createdDateTime,lastModifiedDateTime"
                        : "subject,bodyPreview,start,end,location,attendees,organizer,categories,onlineMeeting,isAllDay,isCancelled,importance,sensitivity,showAs,type,webLink,createdDateTime,lastModifiedDateTime";

                // URLSearchParams: encoding delegated to the stack, no manual %XX.
                // NB: web (`+`) and RN (`%20`) forms differ.
                const agendaParams = new URLSearchParams();
                agendaParams.append("startDateTime", filter.startDate);
                agendaParams.append("endDateTime", filter.endDate);
                agendaParams.append("$select", selectFields);
                agendaParams.append(
                    "$orderby",
                    `start/dateTime ${sortDirection}`,
                );
                agendaParams.append("$top", `${pageSize}`);

                // Keyword filter (subject only) — OData escaping, never encodeURIComponent.
                if (keywords.length > 0) {
                    const keywordFilter = keywords
                        .map(
                            (keyword) =>
                                `contains(subject,'${escapeODataStringValue(keyword)}')`,
                        )
                        .join(" or ");
                    agendaParams.append("$filter", keywordFilter);
                }

                response = await this.callGraphEndPoint(
                    `/me/calendarView?${agendaParams.toString()}`,
                );
            }

            const events = (response?.value ?? []) as Event[];
            const nextCursor: string | undefined =
                response?.["@odata.nextLink"];

            return { events, nextCursor };
        } catch (error) {
            if (error instanceof GraphThrottleError) throw error; // préserve le type 429
            logger.error(
                LOGS.GraphManager,
                `Error in getUserAgendaPage: ${error.message}`,
            );
            throw new Error(`Error in getUserAgendaPage: ${error.message}`);
        }
    }

    /**
     * Hydrates the full `body` (+ bodyPreview) for a set of event ids (AB#158).
     * Used to fetch the body only for the events actually displayed, keeping the scan light.
     *
     * Best-effort **per id**: an id that cannot be resolved (a recurring-occurrence id, or an
     * event deleted between the scan and the hydration → 404) is simply absent from the result,
     * and the caller falls back to the bodyPreview obtained during the scan. A single such
     * failure must NEVER wipe the bodies of the other displayed cards — hence the batch is sent
     * with `tolerateSubRequestFailures: true` (a definitive 4xx sub-response is aggregated, not
     * thrown). 429 is still throttled centrally and rethrown as `GraphThrottleError`.
     *
     * The ids are chunked (Graph caps a single `/$batch` at 20 sub-requests, and sendBatchRequest
     * itself rejects > MAX_BATCHES batches): one batch per chunk keeps any id-list length valid,
     * since hydrateBodies is public API and could be called with more than 60 ids.
     */
    public async getAgendaItemBodies(
        ids: string[],
    ): Promise<Record<string, AgendaItemBody>> {
        logger.info(
            LOGS.GraphManager,
            `getAgendaItemBodies(${ids?.length ?? 0} ids)`,
        );
        const result: Record<string, AgendaItemBody> = {};
        if (!ids || ids.length === 0) return result;

        // Graph $batch hard limit is 20 sub-requests per request → one batch per chunk.
        const BATCH_HYDRATION_CHUNK = 20;

        try {
            for (
                let start = 0;
                start < ids.length;
                start += BATCH_HYDRATION_CHUNK
            ) {
                const chunk = ids.slice(start, start + BATCH_HYDRATION_CHUNK);
                const batchRequests: BatchRequestItem[] = chunk.map(
                    (id, index) => ({
                        id: `${index}`,
                        method: "GET",
                        url: `/me/events/${encodeURIComponent(id)}?$select=body,bodyPreview`,
                    }),
                );

                const batchResponse = await this.sendBatchRequest(
                    { requests: batchRequests },
                    { tolerateSubRequestFailures: true },
                );

                if (batchResponse?.responses) {
                    for (const sub of batchResponse.responses) {
                        const originalId = chunk[Number.parseInt(sub.id, 10)];
                        if (originalId === undefined) continue;
                        if (sub.status >= 200 && sub.status < 300 && sub.body) {
                            result[originalId] = {
                                body: sub.body.body?.content,
                                bodyPreview: sub.body.bodyPreview,
                            };
                        }
                        // Non-2xx → leave absent (graceful fallback to scan bodyPreview).
                    }
                }
            }

            return result;
        } catch (error) {
            if (error instanceof GraphThrottleError) throw error; // préserve le type 429
            // Best-effort hydration: never break the display on a body-fetch failure.
            logger.error(
                LOGS.GraphManager,
                `Error in getAgendaItemBodies: ${error.message}`,
            );
            return result;
        }
    }

    public async createAgendaItem(
        agendaItemData: CreateAgendaItemData,
    ): Promise<boolean> {
        logger.info(
            LOGS.GraphManager,
            `createAgendaItem(${agendaItemData.subject})`,
        );

        try {
            const event: Event = {
                subject: agendaItemData.subject,
                start: {
                    dateTime: agendaItemData.start,
                    timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
                },
                end: {
                    dateTime: agendaItemData.end,
                    timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
                },
                body: {
                    contentType: "html",
                    content: agendaItemData.description || "",
                },
            };

            // Événement « toute la journée » (cf. updateAgendaItem)
            if (agendaItemData.isAllDay !== undefined) {
                event.isAllDay = agendaItemData.isAllDay;
            }

            // Ajouter la localisation si fournie
            if (agendaItemData.location) {
                Object.assign(event, {
                    location: {
                        displayName: agendaItemData.location,
                    },
                });
            }

            // Ajouter les participants si fournis
            if (
                agendaItemData.attendees &&
                agendaItemData.attendees.length > 0
            ) {
                Object.assign(event, {
                    attendees: agendaItemData.attendees.map((email) => ({
                        emailAddress: {
                            address: email,
                        },
                        type: "required",
                    })),
                });
            }

            const request = "/me/calendar/events";
            await this.callGraphEndPoint(request, "POST", event);
            logger.info(LOGS.GraphManager, "Agenda Item created successfully");

            return true;
        } catch (error) {
            logger.error(
                LOGS.GraphManager,
                `Error in createAgendaItem: ${error.message}`,
            );
            return false;
        }
    }

    /**
     * Met à jour un événement existant dans le calendrier de l'utilisateur
     * ═══════════════════════════════════════════════════════════════════════════
     * @input {string} itemId - ID de l'événement à mettre à jour
     * @input {UpdateAgendaItemData} updateData - Données à modifier
     * @output {Promise<boolean>} Succès de la mise à jour
     */
    public async updateAgendaItem(
        itemId: string,
        updateData: UpdateAgendaItemData,
    ): Promise<boolean> {
        logger.info(LOGS.GraphManager, `updateAgendaItem(${itemId})`);

        try {
            // Construction de l'objet de mise à jour
            const updatePayload: Event = {};

            // Champs simples
            if (updateData.subject !== undefined) {
                updatePayload.subject = updateData.subject;
            }

            if (updateData.description !== undefined) {
                updatePayload.body = {
                    contentType: "html",
                    content: updateData.description,
                };
            }

            if (updateData.location !== undefined) {
                updatePayload.location = {
                    displayName: updateData.location,
                };
            }

            // Mise à jour temporelle
            if (updateData.timeUpdate) {
                const timeZone =
                    Intl.DateTimeFormat().resolvedOptions().timeZone;

                updatePayload.start = {
                    dateTime: updateData.timeUpdate.start,
                    timeZone: timeZone,
                };

                updatePayload.end = {
                    dateTime: updateData.timeUpdate.end,
                    timeZone: timeZone,
                };

                if (updateData.timeUpdate.isAllDay !== undefined) {
                    updatePayload.isAllDay = updateData.timeUpdate.isAllDay;
                }
            }

            // Participants
            if (updateData.attendees !== undefined) {
                updatePayload.attendees = updateData.attendees.map((email) => ({
                    emailAddress: {
                        address: email,
                    },
                    type: "required",
                }));
            }

            // Métadonnées
            if (updateData.importance !== undefined) {
                updatePayload.importance = updateData.importance;
            }

            if (updateData.categories !== undefined) {
                updatePayload.categories = updateData.categories;
            }

            if (updateData.sensitivity !== undefined) {
                updatePayload.sensitivity = updateData.sensitivity;
            }

            if (updateData.showAs !== undefined) {
                updatePayload.showAs = updateData.showAs;
            }

            if (updateData.isReminderOn !== undefined) {
                updatePayload.isReminderOn = updateData.isReminderOn;
            }

            if (updateData.reminderMinutesBeforeStart !== undefined) {
                updatePayload.reminderMinutesBeforeStart =
                    updateData.reminderMinutesBeforeStart;
            }

            // Vérifier qu'au moins un champ est à mettre à jour
            if (Object.keys(updatePayload).length === 0) {
                logger.warn(
                    LOGS.DataManager,
                    "No fields to update in agenda item",
                );
                return true; // Considérer comme un succès si rien à changer
            }

            logger.info(LOGS.DataManager, "Update payload", updatePayload);

            // Appel PATCH à l'API Graph
            const request = `/me/calendar/events/${encodeURIComponent(itemId)}`;
            await this.callGraphEndPoint(request, "PATCH", updatePayload);

            logger.info(LOGS.GraphManager, "Agenda Item updated successfully");
            return true;
        } catch (error) {
            logger.error(
                LOGS.GraphManager,
                `Error in updateAgendaItem: ${error.message}`,
            );
            return false;
        }
    }

    /**
     * Supprime un événement du calendrier de l'utilisateur
     * ═══════════════════════════════════════════════════════════════════════════
     * @input {string} itemId - ID de l'événement à supprimer
     * @output {Promise<boolean>} Succès de la suppression
     */
    public async deleteAgendaItem(itemId: string): Promise<boolean> {
        logger.info(LOGS.GraphManager, `deleteAgendaItem(${itemId})`);

        try {
            // Appel DELETE à l'API Graph
            const request = `/me/calendar/events/${encodeURIComponent(itemId)}`;
            await this.callGraphEndPoint(request, "DELETE", null);

            logger.info(LOGS.GraphManager, "Agenda Item deleted successfully");
            return true;
        } catch (error) {
            logger.error(
                LOGS.GraphManager,
                `Error in deleteAgendaItem: ${error.message}`,
            );
            return false;
        }
    }

    /**
     * Accepte un évènement du calendrier de l'utilisateur
     * ═══════════════════════════════════════════════════════════════════════════
     * @input {string} itemId - ID de l'événement à accepter
     * @outpur {Promise<boolean>} Succès de l'acceptation
     */
    public async acceptAgendaItem(itemId: string): Promise<boolean> {
        logger.info(LOGS.GraphManager, `acceptAgendaItem(${itemId})`);

        try {
            const request = `/me/events/${encodeURIComponent(itemId)}/accept`;
            await this.callGraphEndPoint(request, "POST", null);

            logger.info(LOGS.GraphManager, "Agenda Item accepted successfully");
            return true;
        } catch (error) {
            logger.error(
                LOGS.GraphManager,
                `Error in acceptAgendaItem: ${error.message}`,
            );
            return false;
        }
    }

    /**
     * Accepte provisoirement un évènement du calendrier de l'utilisateur
     * ═══════════════════════════════════════════════════════════════════════════
     * @input {string} itemId - ID de l'événement à accepter provisoirement
     * @outpur {Promise<boolean>} Succès de l'acceptation provisoire
     */
    public async tentativelyAcceptAgendaItem(itemId: string): Promise<boolean> {
        logger.info(
            LOGS.GraphManager,
            `tentativelyAcceptAgendaItem(${itemId})`,
        );

        try {
            const request = `/me/events/${encodeURIComponent(itemId)}/tentativelyAccept`;
            await this.callGraphEndPoint(request, "POST", null);

            logger.info(
                LOGS.GraphManager,
                "Agenda Item accepted tentatively successfully",
            );
            return true;
        } catch (error) {
            logger.error(
                LOGS.GraphManager,
                `Error in tentativelyAcceptAgendaItem: ${error.message}`,
            );
            return false;
        }
    }

    /**
     * Refuse un évènement du calendrier de l'utilisateur
     * ═══════════════════════════════════════════════════════════════════════════
     * @input {string} itemId - ID de l'événement à accepter
     * @outpur {Promise<boolean>} Succès de l'acceptation
     */
    public async declineAgendaItem(itemId: string): Promise<boolean> {
        logger.info(LOGS.GraphManager, `declineAgendaItem(${itemId})`);

        try {
            const request = `/me/events/${encodeURIComponent(itemId)}/decline`;
            await this.callGraphEndPoint(request, "POST", null);

            logger.info(LOGS.GraphManager, "Agenda Item declined successfully");
            return true;
        } catch (error) {
            logger.error(
                LOGS.GraphManager,
                `Error in declineAgendaItem: ${error.message}`,
            );
            return false;
        }
    }

    /**
     * Récupère les événements d'agenda modifiés depuis une date donnée
     * ═══════════════════════════════════════════════════════════════════════════
     * @param sinceDate - Date de début pour la recherche (format ISO)
     * @returns Promise<any[]> - Liste des événements modifiés depuis cette date
     */
    public async getUserAgendaModifiedSince(
        sinceDate: string,
    ): Promise<Event[]> {
        logger.info(
            [LOGS.GraphManager],
            `getUserAgendaModifiedSince(${sinceDate})`,
        );

        try {
            // Construction de la requête avec filtre sur lastModifiedDateTime
            // URLSearchParams : encodage délégué à la pile, sans %XX manuel. NB : la forme diffère entre web (`+`) et RN (`%20`) — Graph accepte les deux sur un appel direct ; non validé pour les sous-URLs $batch.
            // lastModifiedDateTime n'est PAS un littéral string OData (pas de quotes) → date brute,
            // pas d'échappement OData, l'encodage est délégué à URLSearchParams.
            const modifiedSinceParams = new URLSearchParams();
            modifiedSinceParams.append(
                "$select",
                "id,categories,subject,bodyPreview,body,start,end,attendees,organizer,onlineMeeting,location,createdDateTime,lastModifiedDateTime,isAllDay,isCancelled,importance,sensitivity,showAs,type,webLink,responseStatus",
            );
            modifiedSinceParams.append(
                "$filter",
                `lastModifiedDateTime ge ${sinceDate}`,
            );
            modifiedSinceParams.append("$orderby", "lastModifiedDateTime desc");
            modifiedSinceParams.append("$top", "1000");
            const request = `/me/calendar/events?${modifiedSinceParams.toString()}`;

            const response = await this.callGraphEndPoint(request);

            if (!response?.value) {
                logger.info(
                    [LOGS.GraphManager],
                    "No agenda events found since last polling",
                );
                return [];
            }

            const result = response.value as Event[];

            return result;
        } catch (error) {
            if (error instanceof GraphThrottleError) throw error; // préserve le type 429
            logger.error(
                [LOGS.GraphManager],
                `Error in getUserAgendaModifiedSince: ${error.message}`,
            );
            throw new Error(
                `Error in getUserAgendaModifiedSince: ${error.message}`,
            );
        }
    }

    // #endregion Agenda

    // #region Documents

    public async getRecentDocuments(
        numberOfDocuments: number = 20,
    ): Promise<DriveItem[]> {
        // const startTime = performance.now();
        logger.info(
            [LOGS.GraphManager, LOGS.AnalysisLifecycle],
            `getRecentDocuments (${numberOfDocuments}) ... `,
        );

        try {
            // const request = `/me/drive/recent?$top=${numberOfDocuments}&$orderby=fileSystemInfo/lastAccessedDateTime DESC`;
            const request = `/me/drive/recent?$top=${numberOfDocuments}&$orderby=fileSystemInfo/lastAccessedDateTime DESC&$select=id,name,description,createdDateTime,lastModifiedDateTime,createdBy,lastModifiedBy,remoteItem,parentReference,fileSystemInfo`;
            const response = await this.callGraphEndPoint(request);

            logger.info(
                [LOGS.GraphManager, LOGS.AnalysisLifecycle],
                `${response.value.length} documents processed`,
            );

            const result = response.value as DriveItem[];
            return result;
        } catch (error) {
            logger.error(
                [LOGS.GraphManager, LOGS.AnalysisLifecycle],
                `Error in getRecentDocuments : ${error}`,
            );
            throw new Error(`Error in getRecentDocuments : ${error}`);
        }
    }

    // #endregion Documents

    // #region Divers

    /**
     * Effectue une requête GET générique à l'API Graph
     * TBD si ce n'est pas utilisé
     * ═══════════════════════════════════════════════════════════
     *
     * @param requestUrl - URL relative de la requête (sans le préfixe de l'URL de l'API Graph)
     * @returns Promise avec le résultat JSON de la requête
     */
    public async getGraphData(requestUrl: string): Promise<any> {
        // const startTime = performance.now();
        logger.info(
            [LOGS.GraphManager, LOGS.AnalysisLifecycle],
            `getGraphData(${requestUrl})`,
        );

        try {
            // Appel à l'API Graph en utilisant la méthode callGraphEndPoint existante
            const result = await this.callGraphEndPoint(requestUrl);

            return result;
        } catch (error) {
            logger.error(
                [LOGS.GraphManager, LOGS.AnalysisLifecycle],
                `Error in getGraphData: ${error}`,
            );
            throw error; // Propager l'erreur pour permettre une gestion appropriée par l'appelant
        }
    }

    // #endregion Divers

    // #endregion Public Methods

    // #region Private Methods

    private mapDateToIDateValue(dateString: string): IDateValue {
        return dateString;
    }

    // Transformation d'un sender Graph vers MailSender
    private mapSender(sender: any): MailSender {
        if (sender) {
            return {
                name: sender.emailAddress.name,
                email: sender.emailAddress.address,
                avatarUrl: null,
            };
        }

        return {
            name: "Inconnu",
            email: "Inconnu",
            avatarUrl: null,
        };
    }

    // Gestion complexe des détails d'événement
    private _mapLinkedEventDetails(
        graphMail: any,
    ): LinkedEventDetails | undefined {
        const currentUserEmail = this.authContext.getUser().email;
        if (!currentUserEmail) {
            logger.error(
                LOGS.GraphManager,
                "Error during mapLinkedEventDetails : unable to retrieve currentUserEmail!",
            );
            throw new Error(
                "Error during mapLinkedEventDetails : unable to retrieve currentUserEmail!",
            );
        }

        if (graphMail["@odata.type"]) {
            if (
                graphMail["@odata.type"] ===
                    "#microsoft.graph.eventMessageRequest" ||
                graphMail["@odata.type"] === "#microsoft.graph.eventMessage" ||
                graphMail["@odata.type"] ===
                    "#microsoft.graph.eventMessageResponse"
            ) {
                const isExpired = (): boolean => {
                    if (!graphMail.startDateTime?.dateTime) return false;
                    const startDate = new Date(
                        graphMail.startDateTime.dateTime,
                    );
                    return startDate < new Date();
                };

                const getUserResponseStatus = (): AttendeeResponseStatus => {
                    if (!graphMail.event?.attendees)
                        return AttendeeResponseStatus.NONE;

                    if (graphMail.event?.isOrganizer)
                        return AttendeeResponseStatus.ORGANIZER;

                    const currentUserAttendee = graphMail.event.attendees.find(
                        (attendee: any) =>
                            attendee.emailAddress?.address?.toLowerCase() ===
                            currentUserEmail.toLowerCase(),
                    );

                    const userResponseStatus =
                        currentUserAttendee?.status?.response || "none";

                    switch (userResponseStatus) {
                        case "organizer":
                            return AttendeeResponseStatus.ORGANIZER;
                        case "tentativelyAccepted":
                            return AttendeeResponseStatus.TENTATIVELY_ACCEPTED;
                        case "accepted":
                            return AttendeeResponseStatus.ACCEPTED;
                        case "declined":
                            return AttendeeResponseStatus.DECLINED;
                        case "notResponded":
                            return AttendeeResponseStatus.NOT_RESPONDED;
                        default:
                            return AttendeeResponseStatus.NONE;
                    }
                };

                const calculateResponseRequested = (): boolean => {
                    const userStatus = getUserResponseStatus();
                    const expired = isExpired();
                    const cancelled = graphMail.event?.isCancelled || false;
                    const messageType = graphMail.meetingMessageType || "";

                    return (
                        messageType === "meetingRequest" &&
                        (userStatus === AttendeeResponseStatus.NONE ||
                            userStatus ===
                                AttendeeResponseStatus.NOT_RESPONDED) &&
                        !expired &&
                        !cancelled
                    );
                };

                const getMessageType = (): EventMessageType => {
                    const odataType = graphMail["@odata.type"] || "";
                    switch (odataType) {
                        case "#microsoft.graph.eventMessageRequest":
                            return EventMessageType.EVENT_MESSAGE_REQUEST;
                        case "#microsoft.graph.eventMessageResponse":
                            return EventMessageType.EVENT_MESSAGE_RESPONSE;
                        case "#microsoft.graph.eventMessage":
                            return EventMessageType.EVENT_MESSAGE;
                        default:
                            return EventMessageType.EVENT_MESSAGE;
                    }
                };

                const getResponseType = (): EventMessageResponseType => {
                    const responseTypeGraph = graphMail.responseType;

                    switch (responseTypeGraph) {
                        case "tentativelyAccepted":
                            return EventMessageResponseType.TENTATIVELY_ACCEPTED;
                        case "accepted":
                            return EventMessageResponseType.ACCEPTED;
                        case "declined":
                            return EventMessageResponseType.DECLINED;
                        default:
                            return EventMessageResponseType.NONE;
                    }
                };

                // By default, Graph sends date in UTC
                const endDateTime = parseGraphDateTimeToIsoString(
                    graphMail.endDateTime,
                );
                const startDateTime = parseGraphDateTimeToIsoString(
                    graphMail.startDateTime,
                );

                return {
                    // `event` est une nav-property Graph non incluse dans le $select de getMailById :
                    // sur un message de réunion fetché par id, graphMail.event est undefined. Défensif
                    // (cohérent avec event?.isCancelled / event?.organizer plus bas) → évite le crash
                    // qui faisait échouer l'hydratation des vues dossier (Sent/Trash).
                    id: graphMail.event?.id || "",
                    meetingMessageType: graphMail.meetingMessageType || "",
                    meetingRequestType: graphMail.type || "",
                    subject: graphMail.subject || "",
                    bodyPreview: graphMail.bodyPreview || "",
                    startDateTime: startDateTime,
                    endDateTime: endDateTime,
                    isAllDay: graphMail.isAllDay || false,
                    recurrence: graphMail.recurrence !== null ? true : false,
                    responseRequested: calculateResponseRequested(),
                    isDelegated: false,
                    location: {
                        displayName: graphMail.location?.displayName || "",
                        locationType: graphMail.location?.locationType || "",
                        uniqueIdType: graphMail.location?.uniqueIdType || "",
                    },
                    userResponseStatus: getUserResponseStatus(),
                    isExpired: isExpired(),
                    isCancelled: graphMail.event?.isCancelled || false,
                    messageType: getMessageType(),
                    responseType: getResponseType(),
                    organizerMail:
                        graphMail.event?.organizer?.emailAddress?.address || "",
                };
            }
        }

        return undefined;
    }

    private mapGraphMailToIMail(graphMail: any): Mail {
        const from = this.mapSender(graphMail.from);
        const to =
            graphMail.toRecipients?.map((recipient: any) =>
                this.mapSender(recipient),
            ) ?? [];
        const cc =
            graphMail.ccRecipients?.map((recipient: any) =>
                this.mapSender(recipient),
            ) ?? [];
        const bcc =
            graphMail.bccRecipients?.map((recipient: any) =>
                this.mapSender(recipient),
            ) ?? [];
        const labelIds = graphMail.categories || [];
        const isImportant = graphMail.importance === "high";
        const sentDateTime = this.mapDateToIDateValue(graphMail.sentDateTime);
        const linkedEventDetails = this._mapLinkedEventDetails(graphMail);
        const hasLinkedMeeting = linkedEventDetails != undefined;

        return {
            id: graphMail.id,
            internetMessageId: graphMail.internetMessageId,
            conversationId: graphMail.conversationId,
            parentFolderId: graphMail.parentFolderId,
            subject: graphMail.subject,
            message: graphMail.body?.content,
            uniqueBody: graphMail.uniqueBody?.content,
            messagePreview: graphMail.bodyPreview,
            isRead: graphMail.isRead,
            from: from,
            to: to,
            cc: cc,
            bcc: bcc,
            labelIds: labelIds,
            isStarred: false,
            isImportant: isImportant,
            sentDateTime: sentDateTime,
            attachments: [],
            isUserProcessed: false,
            isUserEdited: false,
            componentSize: MailComponentSize.SMALL,
            linkedEventDetails: linkedEventDetails,
            isFocus: false,
            smartLabel: undefined,
            hasAttachment: graphMail.hasAttachments,
            hasLinkedMeeting: hasLinkedMeeting,
            links: [],
            score: null,
            manualFocusDate: null,
            sentDateTimeMailvista: null,
            bodyContentType:
                graphMail.body?.contentType?.toLowerCase() === "text"
                    ? BodyContentType.TEXT
                    : BodyContentType.HTML,
            uniqueBodyContentType:
                graphMail.uniqueBody?.contentType?.toLowerCase() === "text"
                    ? BodyContentType.TEXT
                    : BodyContentType.HTML,
        };
    }

    /**
     * Point d'entrée centralisé de TOUS les appels Microsoft Graph (y compris `/$batch`).
     *
     * Gestion du throttling 429 (Too Many Requests) :
     *  - Boucle de **3 tentatives** autour du `fetch`, isolée des retries 401 / réseau
     *    (qui gardent leur sémantique « 1 retry » via `actualRetrying`).
     *  - Délai piloté par le header **`Retry-After`** quand présent (clampé à
     *    [0, 30000ms] pour neutraliser une valeur négative ou aberrante), sinon
     *    **backoff exponentiel** 1s → 2s → 4s (plafond 4s).
     *  - Tentatives intermédiaires journalisées en `logger.warn` ; `logger.error` réservé à l'abandon.
     *  - Après épuisement, lève une {@link GraphThrottleError} typée (`status: 429`,
     *    `isThrottle: true`) — JAMAIS une string. Le `catch` la laisse passer **intacte**
     *    (ni ré-emballage, ni retry réseau), afin que les filets de retry manuels en amont
     *    (AgendaManager, MailManager) puissent la détecter via `isThrottleError` et **ne pas
     *    rejouer** dans la fenêtre de throttle.
     *
     * @throws {GraphThrottleError} si le 429 persiste après les 3 tentatives.
     */
    private async callGraphEndPoint(
        endpoint: string,
        retrying?: boolean,
        addImmutableIdHeader?: boolean,
    ): Promise<any>;
    private async callGraphEndPoint(
        endpoint: string,
        treatAsBlob: boolean,
        retrying?: boolean,
        addImmutableIdHeader?: boolean,
    ): Promise<any>;
    private async callGraphEndPoint(
        endpoint: string,
        method: string,
        body: any,
        retrying?: boolean,
        addImmutableIdHeader?: boolean,
    ): Promise<any>;
    private async callGraphEndPoint(
        endpoint: string,
        methodOrBlob?: string | boolean,
        bodyOrRetrying?: any,
        retrying: boolean = false,
        addImmutableIdHeader: boolean = false,
    ): Promise<any> {
        const graphToken = await this.authContext.getProviderToken();

        // Déterminer si on est en mode legacy et adapter les paramètres
        const isLegacyMode =
            typeof methodOrBlob === "boolean" || methodOrBlob === undefined;

        // Gérer le cas où bodyOrRetrying est en fait le paramètre retrying (2ème signature)
        let actualBody = bodyOrRetrying;
        let actualRetrying = retrying;

        if (isLegacyMode && typeof bodyOrRetrying === "boolean") {
            actualRetrying = bodyOrRetrying;
            actualBody = undefined;
        }

        // Configure les options de la requête
        const options: RequestInit = {
            headers: {
                Authorization: `Bearer ${graphToken}`,
                "Content-Type": "application/json",
                // Une SEULE clé `Prefer` (valeurs séparées par virgule) : deux entrées `'Prefer'` se chevaucheraient
                ...(addImmutableIdHeader && {
                    Prefer: 'IdType="ImmutableId", outlook.timezone="Romance Standard Time"',
                }),
                // TODO : Créer les tickets DevOps
                //  - Fetch TimeZone depuis GraphAPI/mailBoxSettings
                //      - A stocker dans une variable private de ce Manager
                //  - Remplacer addImmutableIdHeader par AdditionnalHeaders string[]
            },
        };

        if (isLegacyMode) {
            // Mode legacy - GET avec option blob optionnelle
            options.method = "GET";
        } else {
            // Nouveau mode - méthode HTTP spécifiée avec body potentiel
            options.method = methodOrBlob as string;
            if (actualBody) {
                options.body = JSON.stringify(actualBody);
            }
        }

        // Backoff 429 (Too Many Requests).
        // Boucle de retry AUTOUR du fetch : honore le header `Retry-After` quand présent,
        // sinon backoff exponentiel (1s → 2s → 4s, plafonné). Isolé du retry 401/network
        // (qui conservent leur sémantique « 1 retry » via actualRetrying). En cas d'échec
        // persistant, lève une GraphThrottleError typée (cf. catch : laissée passer intacte),
        // afin que les filets de retry manuels en amont ne rejouent PAS sur un 429.
        // Délai 429 délégué au helper partagé `computeThrottleDelayMs` (Retry-After
        // secondes + x-ms-retry-after-ms, clamp [0, 30s], sinon backoff exponentiel).
        // Comportement historique préservé : plafond 4s et PAS de jitter
        // (`jitter: false`) — la boucle reste locale pour ne pas toucher au retry 401/réseau.
        const THROTTLE_MAX_RETRIES = 3;
        const THROTTLE_BASE_DELAY_MS = 1000;
        const THROTTLE_MAX_DELAY_MS = 4000;

        let response: Response;
        try {
            let throttleAttempt = 0;

            // eslint-disable-next-line no-constant-condition
            while (true) {
                logger.info(
                    LOGS.GraphManager,
                    `Calling Graph API ${endpoint} with method ${options.method}${actualRetrying ? " (retry attempt)" : ""}${throttleAttempt > 0 ? ` (throttle retry ${throttleAttempt})` : ""}`,
                );

                response = await fetch(graphApiUrl + endpoint, options);

                if (
                    response.status === 429 &&
                    throttleAttempt < THROTTLE_MAX_RETRIES
                ) {
                    throttleAttempt++;

                    const delayMs = computeThrottleDelayMs(
                        response,
                        throttleAttempt,
                        {
                            baseDelayMs: THROTTLE_BASE_DELAY_MS,
                            maxDelayMs: THROTTLE_MAX_DELAY_MS,
                            jitter: false,
                        },
                    );

                    // Tentatives intermédiaires = logger.warn (un retry suit) ; logger.error est réservé à l'abandon.
                    logger.warn(
                        LOGS.GraphManager,
                        `429 Too Many Requests from Graph API — throttle retry ${throttleAttempt}/${THROTTLE_MAX_RETRIES} in ${delayMs}ms (${endpoint})`,
                    );

                    await new Promise((resolve) =>
                        setTimeout(resolve, delayMs),
                    );
                    continue;
                }

                break;
            }

            // 429 toujours présent après épuisement des tentatives → erreur typée.
            if (response.status === 429) {
                logger.error(
                    LOGS.GraphManager,
                    `Received 429 Too Many Requests from Graph API after ${THROTTLE_MAX_RETRIES} retries (${endpoint}) — giving up`,
                );
                throw new GraphThrottleError(endpoint, THROTTLE_MAX_RETRIES);
            }

            // 401 Unauthorized — the provider (IdP) token is outdated.
            // Force a refresh (invalidates the cached provider token) and retry once.
            if (response.status === 401 && !actualRetrying) {
                logger.warn(
                    LOGS.GraphManager,
                    `Received 401 from Graph API (${endpoint}) — provider token outdated, forcing refresh`,
                );

                await this.authContext.forceRefresh();

                if (isLegacyMode) {
                    if (typeof methodOrBlob === "boolean") {
                        return this.callGraphEndPoint(
                            endpoint,
                            methodOrBlob as boolean,
                            true,
                            addImmutableIdHeader,
                        );
                    } else {
                        return this.callGraphEndPoint(
                            endpoint,
                            true,
                            addImmutableIdHeader,
                        );
                    }
                }
                return this.callGraphEndPoint(
                    endpoint,
                    methodOrBlob as string,
                    actualBody,
                    true,
                    addImmutableIdHeader,
                );
            } else if (response.status === 401 && actualRetrying) {
                logger.error(
                    LOGS.GraphManager,
                    `Second 401 on ${endpoint} after retry — session rejected`,
                );
                this.eventBus.emit("auth-token-rejected", {
                    source: "GraphManager.callGraphEndPoint",
                });
                throw new Error(
                    `Graph API 401 Unauthorized — session revoked after retry (${endpoint})`,
                );
            }

            if (!response.ok) {
                // AB#1764 — on attache le status HTTP pour une détection fiable en amont
                // (`isNotFoundError` → suppression idempotente sur 404), sans matcher le message.
                throw makeHttpError(
                    `HTTP error! status: ${response.status}`,
                    response.status,
                );
            }
        } catch (error) {
            // Throttle 429 épuisé : erreur déjà typée et journalisée → la laisser passer
            // intacte (ni ré-emballage en "Graph API call failed", ni retry network).
            if (error instanceof GraphThrottleError) {
                throw error;
            }

            // Log avec toutes les informations disponibles
            const errorDetails = {
                // Infos de base
                name: error.name,
                message: error.message,
                stack: error.stack,

                // Propriétés non-standard (peuvent exister)
                cause: error.cause,
                code: error.code,

                // Contexte de la requête
                endpoint,
                method: methodOrBlob || "GET",
                retryAttempt: actualRetrying || false,

                // État réseau (si accessible)
                networkState: navigator.onLine ? "online" : "offline",

                // Performances réseau (optionnel)
                connectionType:
                    (navigator as any).connection?.effectiveType || "unknown",
            };

            // For all error except those on photos fetch
            if (!endpoint.includes("/photo/$value")) {
                logger.error(
                    LOGS.GraphManager,
                    `Unexpected Error in Graph API call to ${endpoint}: ${error}`,
                );
                logger.info(LOGS.GraphManager, "Error details:", errorDetails);

                // Dans le cas d'une erreur réseau, single retry
                if (error.message === "Failed to fetch") {
                    if (!actualRetrying) {
                        logger.warn(
                            LOGS.GraphManager,
                            `Network error, retrying in 2s endpoint : ${endpoint}`,
                        );
                        await new Promise((resolve) =>
                            setTimeout(resolve, 2000),
                        );

                        // Un seul retry automatique
                        if (isLegacyMode) {
                            if (typeof methodOrBlob === "boolean") {
                                // Signature 2: endpoint + treatAsBlob
                                return this.callGraphEndPoint(
                                    endpoint,
                                    methodOrBlob as boolean,
                                    true,
                                    addImmutableIdHeader,
                                );
                            } else {
                                // Signature 1: endpoint seul
                                return this.callGraphEndPoint(
                                    endpoint,
                                    true,
                                    addImmutableIdHeader,
                                );
                            }
                        } else {
                            // ← CORRECTION : accolade bien placée
                            // Signature 3: endpoint + method + body
                            return this.callGraphEndPoint(
                                endpoint,
                                methodOrBlob as string,
                                actualBody,
                                true,
                                addImmutableIdHeader,
                            );
                        }
                    } else {
                        logger.warn(
                            LOGS.GraphManager,
                            `Network error, already retried once! => Propagating exception`,
                        );
                    }
                } else {
                    logger.error(
                        LOGS.GraphManager,
                        `Error <${error.message}> is not <Failed to fetch> and has no fallback behavior! => Propagating exception.`,
                    );
                }
            }

            // Enrichir l'erreur avant de la propager
            const enhancedError = new Error(
                `Graph API call failed: ${error.message}`,
            );
            (enhancedError as any).details = errorDetails;
            // AB#1764 — préserver le status HTTP à travers le ré-emballage (détection 404 en amont).
            throw inheritHttpStatus(enhancedError, error);
        }

        let result;
        try {
            if (isLegacyMode && methodOrBlob === true) {
                // Si mode legacy et treatAsBlob est true
                result = await response.blob();
            } else if (isLegacyMode && endpoint.includes("/$value")) {
                // Les endpoints /$value retournent du contenu brut (texte MIME pour .msg, binaire pour autres fichiers)
                // On doit utiliser .text() au lieu de .json() pour éviter les erreurs de parsing
                result = await response.text();
            } else {
                result = await response.json();
            }

            logger.info(
                [LOGS.GraphManager],
                `Returnig Graph API result (${endpoint.slice(0, 30)})`,
                result,
            );
            return result;
        } catch (error) {
            logger.warn(
                LOGS.GraphManager,
                `Error parsing Graph API response. Returning result as is`,
            );
            return result;
        }
    }

    /**
     * Envoie un lot de requêtes via le JSON batching Graph (`POST /$batch`),
     * en chunks de 20 (limite Graph), avec retry des sous-réponses transitoires.
     *
     * Point clé : Graph répond HTTP 200 au `/$batch` même lorsque des sous-réponses
     * individuelles sont en 429 (ex : "Application is over its MailboxConcurrency
     * limit." — throttle de concurrence par boîte mail). Le retry 429 de
     * `callGraphEndPoint` ne regarde que le status HTTP externe (200) et ne se
     * déclenche donc JAMAIS sur ces 429 imbriqués. On classe ici chaque sous-réponse :
     *   • 2xx        → succès, conservé
     *   • 429 / 5xx  → transitoire (throttle ou erreur serveur) → rejoué avec backoff
     *   • autre 4xx  → échec définitif (400, 404…), remonté immédiatement
     *
     * Backoff exponentiel par batch : 1s → 2s → 4s … plafonné à 16s. Les 429 de
     * concurrence Graph ne fournissent généralement pas de header `Retry-After`,
     * d'où ce backoff par défaut plutôt qu'une attente pilotée par l'en-tête.
     *
     * @param options.tolerateSubRequestFailures Opt-in best-effort : une sous-réponse en échec
     *        définitif (hors 429/5xx) n'est PAS fatale — elle est agrégée telle quelle dans le
     *        résultat (l'appelant décide quoi en faire, ex. laisser l'id absent) au lieu de faire
     *        échouer tout le batch. Par défaut `false` (comportement historique : throw). Les 429
     *        restent throttlés/rejoués et lèvent une `GraphThrottleError` à l'épuisement.
     *
     * @throws Error si une sous-réponse est en échec définitif (hors 429/5xx) et que
     *         `tolerateSubRequestFailures` est faux, ou si des sous-requêtes restent throttlées
     *         après `MAX_RETRIES` tentatives.
     */
    private async sendBatchRequest(
        batchRequestData: BatchRequestData,
        options?: { tolerateSubRequestFailures?: boolean },
    ): Promise<any> {
        const MAX_BATCH_SIZE = 20;
        const MAX_BATCHES = 3;
        const BATCH_DELAY_MS = 500;
        const MAX_RETRIES = 3; // tentatives par batch sur sous-réponses 429/5xx
        const MAX_BACKOFF_MS = 16000; // plafond du backoff exponentiel (fallback)

        const totalRequests = batchRequestData.requests.length;
        const numberOfBatches = Math.ceil(totalRequests / MAX_BATCH_SIZE);

        // Vérification du nombre maximum de batches
        if (numberOfBatches > MAX_BATCHES) {
            const errorMessage = `La recherche est trop complexe (${totalRequests} requêtes nécessiteraient ${numberOfBatches} batches, maximum autorisé: ${MAX_BATCHES})`;
            logger.error([LOGS.GraphManager], errorMessage);
            throw new Error(errorMessage);
        }

        // Découper en paquets de 20 (même si <= 20, on aura un seul batch)
        const batches: BatchRequestItem[][] = [];
        for (let i = 0; i < totalRequests; i += MAX_BATCH_SIZE) {
            batches.push(
                batchRequestData.requests.slice(i, i + MAX_BATCH_SIZE),
            );
        }

        logger.info(
            [LOGS.GraphManager],
            `Processing ${totalRequests} requests in ${batches.length} batch(es)`,
        );

        // Exécuter les batches séquentiellement et agréger les résultats
        const aggregatedResponses: any[] = [];

        for (let i = 0; i < batches.length; i++) {
            try {
                // Sous-requêtes encore à jouer : tout le batch au départ, puis uniquement
                // les sous-requêtes throttlées (429) ou en erreur serveur (5xx).
                let pendingRequests = batches[i];
                let attempt = 0;
                // Headers de la sous-réponse 429 la plus récente → délai piloté par Retry-After.
                let lastThrottleHeaders:
                    | GraphBatchSubResponse["headers"]
                    | undefined;
                // True si la dernière passe contenait au moins un 429 (vs uniquement des 5xx)
                // → pilote le type d'erreur levée à l'épuisement (GraphThrottleError vs Error).
                let lastBatchThrottled = false;

                // Boucle de retry par batch : `pendingRequests` rétrécit à chaque passe
                // (seules les sous-requêtes transitoires sont conservées).
                while (pendingRequests.length > 0 && attempt < MAX_RETRIES) {
                    // À partir de la 2ᵉ tentative : Retry-After de la sous-réponse 429 si présent,
                    // sinon backoff exponentiel 1s → 2s → 4s … (plafonné à 16s) + jitter ±20%.
                    if (attempt > 0) {
                        const backoffMs = computeThrottleDelayMs(
                            lastThrottleHeaders,
                            attempt,
                            { maxDelayMs: MAX_BACKOFF_MS },
                        );
                        logger.warn(
                            [LOGS.GraphManager],
                            `sendBatchRequest: retry ${attempt}/${MAX_RETRIES - 1} du batch ${i + 1}/${batches.length} — ${pendingRequests.length} sous-requête(s) throttlée(s)/transitoire(s), attente ${backoffMs}ms`,
                        );
                        await new Promise((resolve) =>
                            setTimeout(resolve, backoffMs),
                        );
                    }

                    const response = await this.callGraphEndPoint(
                        "/$batch",
                        "POST",
                        { requests: pendingRequests },
                    );

                    // Réponse inattendue sans corps `responses` : on sort de la boucle de retry.
                    if (!response?.responses) {
                        break;
                    }

                    const retryableRequests: BatchRequestItem[] = [];
                    const definitiveFailures: any[] = [];
                    lastThrottleHeaders = undefined;
                    lastBatchThrottled = false;

                    // Classification de chaque sous-réponse du batch
                    for (const subResponse of response.responses) {
                        // Succès : on conserve la sous-réponse
                        if (
                            subResponse.status >= 200 &&
                            subResponse.status < 300
                        ) {
                            aggregatedResponses.push(subResponse);
                        }
                        // 429 / 5xx : transitoire (throttle de concurrence ou erreur serveur) → rejouer la sous-requête
                        else if (
                            subResponse.status === 429 ||
                            subResponse.status >= 500
                        ) {
                            const throttledRequest = pendingRequests.find(
                                (req) => req.id === subResponse.id,
                            );
                            if (throttledRequest) {
                                retryableRequests.push(throttledRequest);
                            }
                            if (subResponse.status === 429) {
                                lastBatchThrottled = true;
                                if (subResponse.headers) {
                                    lastThrottleHeaders = subResponse.headers;
                                }
                            }
                        }
                        // Autre 4xx : échec définitif (400, 404…), inutile de réessayer
                        else {
                            definitiveFailures.push(subResponse);
                        }
                    }

                    // Échec définitif (hors 429/5xx).
                    if (definitiveFailures.length > 0) {
                        if (options?.tolerateSubRequestFailures) {
                            // Best-effort : on agrège la sous-réponse en échec telle quelle et on
                            // continue — l'appelant filtre les non-2xx (ex. hydratation d'agenda :
                            // un event supprimé (404) ne doit pas faire perdre le body des autres).
                            for (const failure of definitiveFailures) {
                                aggregatedResponses.push(failure);
                            }
                        } else {
                            // Fatal : on remonte l'erreur métier sans réessayer.
                            const failedDetails = definitiveFailures
                                .map((r: any) => {
                                    const originalRequest =
                                        pendingRequests.find(
                                            (req) => req.id === r.id,
                                        );
                                    const errorBody =
                                        r.body?.error?.message ||
                                        r.body?.error?.code ||
                                        `Status ${r.status}`;
                                    return `[id: ${r.id}, url: ${originalRequest?.url || "unknown"}, error: ${errorBody}]`;
                                })
                                .join(", ");

                            const errorMessage = `La recherche a échoué pour ${definitiveFailures.length} requête(s): ${failedDetails}`;
                            logger.error([LOGS.GraphManager], errorMessage);
                            logger.info(
                                [LOGS.GraphManager],
                                "Failed batch responses",
                                definitiveFailures,
                            );
                            throw new Error(errorMessage);
                        }
                    }

                    pendingRequests = retryableRequests;
                    attempt++;
                }

                // Sous-requêtes toujours throttlées après MAX_RETRIES tentatives : échec définitif.
                if (pendingRequests.length > 0) {
                    const failedDetails = pendingRequests
                        .map((req) => `[id: ${req.id}, url: ${req.url}]`)
                        .join(", ");
                    const errorMessage = `La recherche a échoué après ${MAX_RETRIES} tentatives pour ${pendingRequests.length} requête(s) toujours throttlée(s) (429/5xx): ${failedDetails}`;
                    logger.error([LOGS.GraphManager], errorMessage);
                    // Échec dû au throttling 429 (et non à des 5xx) → erreur typée, pour que les
                    // filets de retry manuels en amont (isThrottleError) ne rejouent PAS dans la
                    // fenêtre de throttle. Sinon (5xx persistants) on garde une Error générique.
                    if (lastBatchThrottled) {
                        throw new GraphThrottleError("/$batch", MAX_RETRIES);
                    }
                    throw new Error(errorMessage);
                }

                // Attendre 500ms avant le prochain batch (sauf pour le dernier)
                if (i < batches.length - 1) {
                    await new Promise((resolve) =>
                        setTimeout(resolve, BATCH_DELAY_MS),
                    );
                }
            } catch (error) {
                logger.error(
                    LOGS.GraphManager,
                    `Error executing batch ${i + 1}/${batches.length}`,
                    error,
                );
                throw error;
            }
        }

        return { responses: aggregatedResponses };
    }

    private async _blobToBase64(blob: Blob): Promise<string> {
        return new Promise((resolve) => {
            const reader = new FileReader();
            reader.onload = () => resolve(reader.result as string);
            reader.readAsDataURL(blob);
        });
    }

    // TBD if not necessary
    private _clientSideFilter(emails: any[], filter: EmailSearchFilter): any[] {
        let startDate: Date | null = null;
        let endDate: Date | null = null;

        if (filter.dateRange) {
            if (typeof filter.dateRange.start === "string") {
                startDate = new Date(filter.dateRange.start);
            } else if (filter.dateRange.start instanceof Date) {
                startDate = filter.dateRange.start;
            }

            if (typeof filter.dateRange.end === "string") {
                endDate = new Date(filter.dateRange.end);
            } else if (filter.dateRange.end instanceof Date) {
                endDate = filter.dateRange.end;
            }

            if (startDate !== null && endDate !== null) {
                // Vérification de la validité des dates
                if (
                    Number.isNaN(startDate!.getTime()) ||
                    Number.isNaN(endDate!.getTime())
                ) {
                    throw new Error("Invalid date format in dateRange");
                }
            }
        }

        return emails.filter((email) => {
            // Filtrage des éléments avant startDate
            if (startDate) {
                // Vérification qu'au moins un terme correspond dans le sujet ou le corps
                const hasMatch = new Date(email.sentDateTime) >= startDate;

                if (!hasMatch) return false;
            }

            // Filtrage des éléments apres endDate
            if (endDate) {
                // Vérification qu'au moins un terme correspond dans le sujet ou le corps
                const hasMatch = new Date(email.sentDateTime) <= endDate;

                if (!hasMatch) return false;
            }

            // Filtrage des éléments non lus
            if (filter.isUnread) {
                // Vérification qu'au moins un terme correspond dans le sujet ou le corps
                const hasMatch = email.isRead === !filter.isUnread;

                if (!hasMatch) return false;
            }

            // Ajoutez ici d'autres critères de filtrage si nécessaire
            return true;
        });
    }

    // #endregion Private Methods
}
